import {
  Application,
  Assets,
  Container,
  ContainerChild,
  FederatedPointerEvent,
  GenerateTextureOptions,
  Graphics,
  groupD8,
  IRenderLayer,
  PerspectiveMesh,
  Point,
  Sprite,
  StrokeInput,
  Texture,
} from "pixi.js";
import { Aspect } from "./Aspect";
import { Dimension2D } from "./Dimension2D";
import { DomicilePosition } from "./DomicilePosition";
import {
  Door,
  DoorType,
  DoorOpening,
  Hardware,
  HexColor,
  DoorPoint,
} from "../models";
import { LoadDoorOpeningsOptions } from "./LoadDoorOpeningsOptions";
import { LoadDoorOptions } from "./LoadDoorOptions";
import { SingleDoorOptions } from "./SingleDoorOptions";
import EventEmitter from "eventemitter3";
import { DragPointData } from "./DragPointData";
import { DragDoorData } from "./DragDoorData";
import { DoorDesignerViewerEvents } from "./DoorDesignerViewerEvents";
import { ModificationPointType } from "./ModificationPointType";
import { Calculator, Mapper, TypeConverter } from "../helpers";
import { Cursors, EventModes, Events, Labels } from "../constants";
import { DoorData } from "./DoorData";
import { RotationAngle } from "./RotationAngle";
import { ImageFormat } from "./ImageFormat";
import "pixi.js/unsafe-eval";

export class Viewer extends EventEmitter<DoorDesignerViewerEvents> {
  public pixiApplication: Application = new Application();
  private doorsContainer = new Container();
  private magnificationContainer = new Container();
  private domicileSprite: Sprite = new Sprite();
  private domicilePosition: DomicilePosition = DomicilePosition.Foreground;
  private doorsData: DoorData[] = [];
  private dragPointData: DragPointData = null;
  private dragDoorData: DragDoorData = null;
  readonly minimumDistance = 4;
  readonly marginDistance = 0;
  private endDragWhenOutOfImage: boolean = false;
  private selectedDoorId: number = -1;
  private dimension: Dimension2D = new Dimension2D(0, 0);
  private offsetPoint: DoorPoint = new DoorPoint(0, 0);
  private currentDoorId = 0;
  private aspect: Aspect = Aspect.Fit;

  private loadDoorOptions: LoadDoorOptions =
    Calculator.generateDefaultLoadDoorOptions();

  public async init(
    htmlElement: HTMLElement,
    dimension: Dimension2D,
    backgroundColor: HexColor
  ): Promise<void> {
    this.dimension = dimension;

    await this.pixiApplication.init({
      width: this.dimension.width,
      height: this.dimension.height,
      backgroundColor: backgroundColor.color,
      backgroundAlpha: backgroundColor.alpha,
      antialias: true,
      resolution: window.devicePixelRatio,
    });

    htmlElement.appendChild(this.pixiApplication.canvas);

    this.pixiApplication.stage.label = Labels.Stage;
    this.pixiApplication.stage.eventMode = EventModes.Static;
    this.pixiApplication.stage.hitArea = this.pixiApplication.screen;
    this.pixiApplication.stage.on(
      Events.PointerUp,
      (event: FederatedPointerEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this.onDragEnd();
      },
      this
    );
    this.pixiApplication.stage.on(
      Events.PointerUpOutside,
      (event: FederatedPointerEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this.onDragEnd();
      },
      this
    );

    this.pixiApplication.stage.on(
      Events.PointerDown,
      (event: FederatedPointerEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedDoorId = this.getSelectedDoorId();

        if (selectedDoorId < 0) {
          return;
        }

        this.selectDoorInternal(-1, true);
      },
      this
    );

    this.domicileSprite.label = Labels.DomicileSprite;
    this.domicileSprite.interactive = false;
    this.domicileSprite.interactiveChildren = false;
    this.domicileSprite.eventMode = EventModes.None;
    this.pixiApplication.stage.addChild(this.domicileSprite);

    this.doorsContainer.label = Labels.Doors;
    this.doorsContainer.interactive = false;
    this.pixiApplication.stage.addChild(this.doorsContainer);

    this.magnificationContainer.label = Labels.MagnificationContainer;
    this.magnificationContainer.interactive = false;
    this.magnificationContainer.interactiveChildren = false;
    this.magnificationContainer.eventMode = EventModes.None;
    this.magnificationContainer.visible = false;
    this.pixiApplication.stage.addChild(this.magnificationContainer);
  }

  public async loadDomicileFromUrl(
    url: string,
    aspect: Aspect
  ): Promise<Dimension2D> {
    const texture = await Assets.load(url);
    this.domicileSprite.texture = texture;

    return this.loadDomicileTexture(texture, aspect);
  }

  public async loadDomicileFromFile(
    file: File,
    aspect: Aspect
  ): Promise<Dimension2D> {
    const base64 = await TypeConverter.fileToBase64(file);

    return await this.loadDomicileFromBase64(base64, aspect);
  }

  public async loadDomicileFromBase64(
    base64: string,
    aspect: Aspect
  ): Promise<Dimension2D> {
    const texture = await Assets.load(base64);
    return this.loadDomicileTexture(texture, aspect);
  }

  public async loadDomicileFromByteArray(
    uint8Array: Uint8Array,
    aspect: Aspect
  ): Promise<Dimension2D> {
    const base64 = TypeConverter.uint8ArrayToBase64(uint8Array);

    return await this.loadDomicileFromBase64(base64, aspect);
  }

  public async loadDomicileFromBlob(
    content: Blob,
    aspect: Aspect
  ): Promise<Dimension2D> {
    const blobUrl: string = URL.createObjectURL(content);

    const texture = await Assets.load({
      src: blobUrl,
      format: "png",
      loadParser: "loadTextures",
    });
    return this.loadDomicileTexture(texture, aspect);
  }

  private loadDomicileTexture(texture: Texture, aspect: Aspect): Dimension2D {
    this.domicileSprite.texture = texture;
    this.aspect = aspect;

    const aspectRatio = new Dimension2D(
      texture.frame.width,
      texture.frame.height
    );

    const boundingSize = new Dimension2D(
      this.pixiApplication.screen.width,
      this.pixiApplication.screen.height
    );

    const aspectRatioDimension = Calculator.calculateAspectRatio(
      aspect,
      aspectRatio,
      boundingSize
    );

    this.offsetPoint = new DoorPoint(
      (this.pixiApplication.screen.width - aspectRatioDimension.width) / 2,
      (this.pixiApplication.screen.height - aspectRatioDimension.height) / 2
    );

    this.domicileSprite.x = this.offsetPoint.x;
    this.domicileSprite.y = this.offsetPoint.y;
    this.domicileSprite.width = aspectRatioDimension.width;
    this.domicileSprite.height = aspectRatioDimension.height;

    return aspectRatioDimension;
  }

  public clearDomicile(): void {
    this.domicileSprite.texture = Texture.EMPTY;
  }

  public setDomicilePosition(position: DomicilePosition): void {
    this.domicilePosition = position;

    this.doorsContainer.zIndex = 0;
    this.domicileSprite.zIndex = 1;
    this.magnificationContainer.zIndex = 2;

    if (this.domicilePosition === DomicilePosition.Background) {
      this.doorsContainer.zIndex = 1;
      this.domicileSprite.zIndex = 0;
      this.magnificationContainer.zIndex = 2;
    }
  }

  public setDomicilePositionByTransparent(
    hasTransparentOpenings: boolean
  ): void {
    if (hasTransparentOpenings) {
      this.setDomicilePosition(DomicilePosition.Foreground);
    } else {
      this.setDomicilePosition(DomicilePosition.Background);
    }
  }

  public setBackgroundColor(backgroundColor: HexColor): void {
    this.pixiApplication.renderer.background.color = backgroundColor.color;
    this.pixiApplication.renderer.background.alpha = backgroundColor.alpha;
  }

  private generateNextDoorId(): number {
    return this.currentDoorId++;
  }

  public async addDoor(
    door: Door,
    dimension?: Dimension2D,
    loadDoorOptions?: LoadDoorOptions
  ): Promise<void> {
    const doorId = this.generateNextDoorId();

    const mergedLoadDoorOptions: LoadDoorOptions = {
      ...this.loadDoorOptions,
      ...loadDoorOptions,
    };

    const doorContainer = new Container();
    doorContainer.interactive = false;
    doorContainer.interactiveChildren = true;
    doorContainer.eventMode = EventModes.Passive;
    doorContainer.label = Labels.Door;
    this.doorsContainer.addChild(doorContainer);

    if (!dimension) {
      dimension = this.dimension;
    }

    const doorOpening = Calculator.calculateDoorOpeningSize(
      door,
      this.dimension
    );

    let doorTexture = (await Assets.load(door.imageUrl)) as Texture;
    let hardwareTexture: Texture = null;

    if (door.doorType === DoorType.ArchedGarageDoor) {
      const archDoorImageContainer = new Container();
      archDoorImageContainer.label = Labels.ArchDoorImageContainer;
      archDoorImageContainer.interactive = false;
      archDoorImageContainer.interactiveChildren = false;
      archDoorImageContainer.eventMode = EventModes.None;
      archDoorImageContainer.visible = false;
      doorContainer.addChild(archDoorImageContainer);

      const archDoorMaskGraphics = this.generateArchMaskGraphics(
        Labels.ArchMask,
        door,
        doorTexture.width,
        doorTexture.height
      );
      archDoorImageContainer.addChild(archDoorMaskGraphics);

      const archDoorSprite = Sprite.from(doorTexture);
      archDoorSprite.label = Labels.ArchSprite;
      archDoorSprite.interactive = false;
      archDoorSprite.interactiveChildren = false;
      archDoorSprite.eventMode = EventModes.None;
      archDoorSprite.mask = archDoorMaskGraphics;
      archDoorImageContainer.addChild(archDoorSprite);

      doorTexture = this.generateTextureFromImageContainer(
        archDoorImageContainer
      );
    }

    const doorMesh = this.addDoorMesh(
      doorId,
      door.doorType,
      doorContainer,
      doorOpening,
      this.dimension,
      mergedLoadDoorOptions,
      doorTexture
    );

    if (door.doorType === DoorType.ChamferedGarageDoor) {
      const chamferMaskGraphics = this.addChamferMaskGraphics(
        doorOpening,
        doorContainer
      );

      doorMesh.mask = chamferMaskGraphics;
    }

    if (door.hardware && door.hardware.imageUrl) {
      hardwareTexture = (await Assets.load(door.hardware.imageUrl)) as Texture;

      this.addHardware(
        door,
        doorContainer,
        doorOpening,
        doorMesh,
        hardwareTexture
      );
    }

    this.addPerimeterGraphics(
      doorContainer,
      doorOpening,
      this.dimension,
      mergedLoadDoorOptions
    );

    this.addModificationPointsGraphics(
      doorId,
      doorContainer,
      doorOpening,
      this.dimension,
      mergedLoadDoorOptions,
      door.doorType
    );

    // This is only done if all doors were removed and a new one is being added
    // since it is done initially on "loadDoorOpenings"
    if (
      !this.loadDoorOptions.allowNoDoorsSelected &&
      doorId !== 0 &&
      this.selectedDoorId === -1
    ) {
      this.selectDoorInternal(doorId, false);
    }

    this.doorsData.push({
      doorId: doorId,
      door: door,
      currentDoorOpening: doorOpening,
      loadDoorOptions: mergedLoadDoorOptions,
      container: doorContainer,
    });
  }

  private addHardware(
    door: Door,
    doorContainer: Container,
    doorOpening: DoorOpening,
    doorMesh: PerspectiveMesh,
    hardwareTexture: Texture
  ) {
    if (door.doorType === DoorType.ArchedGarageDoor) {
      const archHardwareImageContainer = new Container();
      archHardwareImageContainer.label = Labels.ArchHardwareImageContainer;
      archHardwareImageContainer.interactive = false;
      archHardwareImageContainer.interactiveChildren = false;
      archHardwareImageContainer.eventMode = EventModes.None;
      archHardwareImageContainer.visible = false;
      doorContainer.addChild(archHardwareImageContainer);

      const archHardwareMaskGraphics = this.generateArchMaskGraphics(
        Labels.ArchMask,
        door,
        hardwareTexture.width,
        hardwareTexture.height
      );
      archHardwareImageContainer.addChild(archHardwareMaskGraphics);

      const archHardwareSprite = Sprite.from(hardwareTexture);
      archHardwareSprite.label = Labels.ArchSprite;
      archHardwareSprite.interactive = false;
      archHardwareSprite.interactiveChildren = false;
      archHardwareSprite.eventMode = EventModes.None;
      archHardwareSprite.mask = archHardwareMaskGraphics;
      archHardwareImageContainer.addChild(archHardwareSprite);

      hardwareTexture = this.generateTextureFromImageContainer(
        archHardwareImageContainer
      );
    }

    const hardwareMesh = this.addHardwareMesh(
      door.doorType,
      doorContainer,
      door.hardware,
      doorOpening,
      this.dimension,
      hardwareTexture
    );

    if (door.doorType === DoorType.ChamferedGarageDoor) {
      if (hardwareMesh) {
        hardwareMesh.mask = doorMesh.mask;
      }
    }
  }

  private addChamferMaskGraphics(
    doorOpening: DoorOpening,
    doorContainer: Container
  ): Graphics {
    const chamferMaskGraphics = new Graphics();
    chamferMaskGraphics.label = Labels.ChamferMask;
    chamferMaskGraphics.interactive = false;
    chamferMaskGraphics.interactiveChildren = false;
    chamferMaskGraphics.eventMode = EventModes.None;
    chamferMaskGraphics.position.set(this.offsetPoint.x, this.offsetPoint.y);
    doorContainer.addChild(chamferMaskGraphics);

    chamferMaskGraphics
      .moveTo(doorOpening.topLeft.x, doorOpening.topLeft.y)
      .lineTo(doorOpening.chamferEdgeLeft.x, doorOpening.chamferEdgeLeft.y)
      .lineTo(doorOpening.chamferEdgeRight.x, doorOpening.chamferEdgeRight.y)
      .lineTo(doorOpening.topRight.x, doorOpening.topRight.y)
      .lineTo(doorOpening.bottomRight.x, doorOpening.bottomRight.y)
      .lineTo(doorOpening.bottomLeft.x, doorOpening.bottomLeft.y)
      .lineTo(doorOpening.topLeft.x, doorOpening.topLeft.y)
      .fill({
        color: HexColor.White.color,
        alpha: HexColor.White.alpha,
      });

    return chamferMaskGraphics;
  }

  private generateArchMaskGraphics(
    label: string,
    door: Door,
    width: number,
    height: number
  ): Graphics {
    const archMaskGraphics = new Graphics();
    archMaskGraphics.label = label;
    archMaskGraphics.interactive = false;
    archMaskGraphics.interactiveChildren = false;
    archMaskGraphics.eventMode = EventModes.None;

    this.drawArchMaskGraphics(archMaskGraphics, door, width, height);

    return archMaskGraphics;
  }

  private drawArchMaskGraphics(
    archMaskGraphics: Graphics,
    doorOpening: DoorOpening,
    width: number,
    height: number
  ): void {
    const doorHeight = Math.sqrt(
      (doorOpening.bottomLeft.y - doorOpening.topLeft.y) ** 2 +
        (doorOpening.bottomLeft.x - doorOpening.topLeft.x) ** 2
    );

    const yOffset =
      height *
      ((doorOpening.archEdgeLeft.y - doorOpening.topLeft.y) / doorHeight);

    archMaskGraphics
      .moveTo(0, yOffset)
      .quadraticCurveTo(width / 2, -yOffset, width, yOffset, 1)
      .lineTo(width, height)
      .lineTo(0, height)
      .lineTo(0, yOffset)
      .fill({
        color: HexColor.White.color,
        alpha: HexColor.White.alpha,
      });
  }

  private addModificationMagnificationGraphics(loadOptions: LoadDoorOptions) {
    this.clearMagnification();

    if (!loadOptions.displayMagnification) {
      return;
    }

    loadOptions.modificationMagnificationFillColor =
      HexColor.populateWithDefaultIfNeeded(
        loadOptions.modificationMagnificationFillColor
      );

    loadOptions.modificationMagnificationBorderColor =
      HexColor.populateWithDefaultIfNeeded(
        loadOptions.modificationMagnificationBorderColor
      );

    if (
      loadOptions.modificationMagnificationRadius === undefined ||
      loadOptions.modificationMagnificationRadius === null
    ) {
      loadOptions.modificationMagnificationRadius = 40;
    }

    if (
      loadOptions.modificationMagnificationBorderWidth === undefined ||
      loadOptions.modificationMagnificationBorderWidth === null
    ) {
      loadOptions.modificationMagnificationBorderWidth = 0;
    }

    const originalTexture = this.domicileSprite.texture;
    const source = originalTexture.source;
    const frame = originalTexture.frame;

    const texture = new Texture({
      source,
      frame,
    });

    const maskGraphics = new Graphics();
    maskGraphics.label = Labels.MagnificationMask;
    maskGraphics.interactive = false;
    maskGraphics.interactiveChildren = false;
    maskGraphics.eventMode = EventModes.None;
    maskGraphics
      .circle(
        loadOptions.modificationMagnificationRadius,
        loadOptions.modificationMagnificationRadius,
        loadOptions.modificationMagnificationRadius
      )
      .fill({
        color: HexColor.White.color,
        alpha: HexColor.White.alpha,
      });
    this.magnificationContainer.addChild(maskGraphics);

    const magnifiedDomicileSprite = new Sprite(texture);
    magnifiedDomicileSprite.label = Labels.MagnificationImage;
    magnifiedDomicileSprite.interactive = false;
    magnifiedDomicileSprite.interactiveChildren = false;
    magnifiedDomicileSprite.eventMode = EventModes.None;
    magnifiedDomicileSprite.width =
      this.dimension.width * loadOptions.modificationMagnificationFactor;
    magnifiedDomicileSprite.height =
      this.dimension.height * loadOptions.modificationMagnificationFactor;
    this.magnificationContainer.mask = maskGraphics;
    this.magnificationContainer.addChild(magnifiedDomicileSprite);

    if (loadOptions.modificationMagnificationBorderWidth >= 1) {
      const modificationMagnificationPerimeterGraphics = new Graphics();
      modificationMagnificationPerimeterGraphics.label =
        Labels.MagnificationPerimeter;
      modificationMagnificationPerimeterGraphics.interactive = false;
      modificationMagnificationPerimeterGraphics.interactiveChildren = false;
      modificationMagnificationPerimeterGraphics.eventMode = EventModes.None;

      modificationMagnificationPerimeterGraphics
        .arc(
          loadOptions.modificationMagnificationRadius,
          loadOptions.modificationMagnificationRadius,
          loadOptions.modificationMagnificationRadius -
            loadOptions.modificationMagnificationBorderWidth / 2,
          0,
          Math.PI * 2
        )
        .stroke({
          color: loadOptions.modificationMagnificationBorderColor.color,
          alpha: loadOptions.modificationMagnificationBorderColor.alpha,
          width: loadOptions.modificationMagnificationBorderWidth,
        });

      this.magnificationContainer.addChild(
        modificationMagnificationPerimeterGraphics
      );
    }

    const magnificationTargetContainer = new Container();
    magnificationTargetContainer.label = Labels.MagnificationTargetContainer;
    magnificationTargetContainer.interactive = false;
    magnificationTargetContainer.interactiveChildren = false;
    magnificationTargetContainer.eventMode = EventModes.None;
    this.magnificationContainer.addChild(magnificationTargetContainer);
  }

  private generatePerspectiveMesh(
    doorType: DoorType,
    doorOpening: DoorOpening,
    dimension: Dimension2D,
    texture: Texture
  ): PerspectiveMesh {
    const xPosition = (this.pixiApplication.screen.width - dimension.width) / 2;
    const yPosition =
      (this.pixiApplication.screen.height - dimension.height) / 2;

    if (doorType === DoorType.ChamferedGarageDoor) {
      const chamferEdgeLine = Calculator.calculateLineFormula(
        doorOpening.chamferEdgeLeft,
        doorOpening.chamferEdgeRight
      );

      const leftLine = Calculator.calculateLineFormula(
        doorOpening.topLeft,
        doorOpening.bottomLeft
      );

      const rightLine = Calculator.calculateLineFormula(
        doorOpening.topRight,
        doorOpening.bottomRight
      );

      const topLeftPoint = Calculator.calculateLinesIntersection(
        leftLine,
        chamferEdgeLine
      );

      const topRightPoint = Calculator.calculateLinesIntersection(
        rightLine,
        chamferEdgeLine
      );

      return new PerspectiveMesh({
        texture,
        x0: topLeftPoint.x,
        y0: topLeftPoint.y,
        x1: topRightPoint.x,
        y1: topRightPoint.y,
        x2: doorOpening.bottomRight.x,
        y2: doorOpening.bottomRight.y,
        x3: doorOpening.bottomLeft.x,
        y3: doorOpening.bottomLeft.y,
        x: xPosition,
        y: yPosition,
      });
    }

    return new PerspectiveMesh({
      texture,
      x0: doorOpening.topLeft.x,
      y0: doorOpening.topLeft.y,
      x1: doorOpening.topRight.x,
      y1: doorOpening.topRight.y,
      x2: doorOpening.bottomRight.x,
      y2: doorOpening.bottomRight.y,
      x3: doorOpening.bottomLeft.x,
      y3: doorOpening.bottomLeft.y,
      x: xPosition,
      y: yPosition,
    });
  }

  private addDoorMesh(
    doorId: number,
    doorType: DoorType,
    container: Container,
    doorOpening: DoorOpening,
    dimension: Dimension2D,
    loadOptions: LoadDoorOptions,
    doorTexture: Texture
  ): PerspectiveMesh {
    const doorPerspectiveMesh = this.generatePerspectiveMesh(
      doorType,
      doorOpening,
      dimension,
      doorTexture
    );

    doorPerspectiveMesh.label = Labels.DoorImage;
    doorPerspectiveMesh.interactiveChildren = false;

    if (loadOptions.isDoorDraggable || loadOptions.isDoorSelectable) {
      doorPerspectiveMesh.interactive = true;
      doorPerspectiveMesh.cursor = Cursors.Grab;
      doorPerspectiveMesh.eventMode = EventModes.Static;

      doorPerspectiveMesh.on(
        Events.PointerDown,
        (event: FederatedPointerEvent) => {
          event.preventDefault();
          event.stopPropagation();

          this.selectDoorInternal(doorId, true);

          if (loadOptions.isDoorDraggable) {
            doorPerspectiveMesh.cursor = Cursors.Grabbing;

            const offsetPoint = new Point(
              event.global.x -
                doorPerspectiveMesh.geometry.corners[0] -
                doorPerspectiveMesh.position.x,
              event.global.y -
                doorPerspectiveMesh.geometry.corners[1] -
                doorPerspectiveMesh.position.y
            );

            this.dragDoorData = {
              target: container,
              doorId: doorId,
              point: event.global.clone(),
              offsetPoint: offsetPoint,
            };

            this.pixiApplication.stage.on(
              Events.PointerMove,
              this.onDoorDragMove,
              this
            );
          }
        },
        this
      );
    } else {
      doorPerspectiveMesh.interactive = false;
      doorPerspectiveMesh.eventMode = EventModes.None;
    }

    container.addChild(doorPerspectiveMesh);

    return doorPerspectiveMesh;
  }

  private generateTextureFromImageContainer(container: Container): Texture {
    const options: GenerateTextureOptions = {
      target: container,
      resolution: 2,
      antialias: true,
    };

    return this.pixiApplication.renderer.generateTexture(options);
  }

  private addHardwareMesh(
    doorType: DoorType,
    container: Container,
    hardware: Hardware,
    doorOpening: DoorOpening,
    dimension: Dimension2D,
    hardwareTexture: Texture
  ): PerspectiveMesh {
    if (!hardware) {
      return null;
    }

    const hardwarePerspectiveMesh = this.generatePerspectiveMesh(
      doorType,
      doorOpening,
      dimension,
      hardwareTexture
    );

    hardwarePerspectiveMesh.label = Labels.HardwareImage;
    hardwarePerspectiveMesh.interactive = false;
    hardwarePerspectiveMesh.interactiveChildren = false;
    hardwarePerspectiveMesh.eventMode = EventModes.None;

    container.addChild(hardwarePerspectiveMesh);

    return hardwarePerspectiveMesh;
  }

  private addPerimeterGraphics(
    container: Container,
    doorOpening: DoorOpening,
    dimension: Dimension2D,
    loadOptions: LoadDoorOptions
  ): void {
    if (!loadOptions.displayPerimeter) {
      return;
    }

    loadOptions.perimeterColor = HexColor.populateWithDefaultIfNeeded(
      loadOptions.perimeterColor
    );

    if (
      loadOptions.perimeterWidth === undefined ||
      loadOptions.perimeterWidth === null ||
      loadOptions.perimeterWidth < 0
    ) {
      loadOptions.perimeterWidth = 0;
    }

    const perimeterGraphics = new Graphics({
      x: (this.pixiApplication.screen.width - dimension.width) / 2,
      y: (this.pixiApplication.screen.height - dimension.height) / 2,
    });

    perimeterGraphics.label = Labels.DoorPerimeter;
    perimeterGraphics.interactive = false;
    perimeterGraphics.interactiveChildren = false;
    perimeterGraphics.eventMode = EventModes.None;

    perimeterGraphics
      .poly([
        doorOpening.topLeft.x,
        doorOpening.topLeft.y,
        doorOpening.topRight.x,
        doorOpening.topRight.y,
        doorOpening.bottomRight.x,
        doorOpening.bottomRight.y,
        doorOpening.bottomLeft.x,
        doorOpening.bottomLeft.y,
      ])
      .stroke({
        color: loadOptions.perimeterColor.color,
        alpha: loadOptions.perimeterColor.alpha,
        width: loadOptions.perimeterWidth,
      });

    container.addChild(perimeterGraphics);
  }

  private addModificationPointsGraphics(
    doorId: number,
    container: Container,
    doorOpening: DoorOpening,
    dimension: Dimension2D,
    loadOptions: LoadDoorOptions,
    doorType: DoorType
  ): void {
    if (!loadOptions.displayModificationPoints) {
      return;
    }

    const modificationPointsContainer = new Container();
    modificationPointsContainer.label = Labels.ModificationPoints;
    modificationPointsContainer.interactive = false;
    modificationPointsContainer.interactiveChildren = true;
    modificationPointsContainer.eventMode = EventModes.Passive;

    loadOptions.modificationPointFillColor =
      HexColor.populateWithDefaultIfNeeded(
        loadOptions.modificationPointFillColor
      );
    loadOptions.modificationPointBorderColor =
      HexColor.populateWithDefaultIfNeeded(
        loadOptions.modificationPointBorderColor
      );

    if (
      loadOptions.modificationPointRadius === undefined ||
      loadOptions.modificationPointRadius === null
    ) {
      loadOptions.modificationPointRadius = 5;
    }

    if (
      loadOptions.modificationPointBorderWidth === undefined ||
      loadOptions.modificationPointBorderWidth === null
    ) {
      loadOptions.modificationPointBorderWidth = 5;
    }

    this.addModificationPointGraphics(
      doorId,
      doorType,
      modificationPointsContainer,
      dimension,
      doorOpening,
      ModificationPointType.TopLeft,
      loadOptions
    );

    this.addModificationPointGraphics(
      doorId,
      doorType,
      modificationPointsContainer,
      dimension,
      doorOpening,
      ModificationPointType.TopRight,
      loadOptions
    );

    this.addModificationPointGraphics(
      doorId,
      doorType,
      modificationPointsContainer,
      dimension,
      doorOpening,
      ModificationPointType.BottomRight,
      loadOptions
    );

    this.addModificationPointGraphics(
      doorId,
      doorType,
      modificationPointsContainer,
      dimension,
      doorOpening,
      ModificationPointType.BottomLeft,
      loadOptions
    );

    if (doorType === DoorType.ArchedGarageDoor) {
      this.addModificationPointGraphics(
        doorId,
        doorType,
        modificationPointsContainer,
        dimension,
        doorOpening,
        ModificationPointType.ArchEdgeLeft,
        loadOptions
      );
    } else if (doorType === DoorType.ChamferedGarageDoor) {
      this.addModificationPointGraphics(
        doorId,
        doorType,
        modificationPointsContainer,
        dimension,
        doorOpening,
        ModificationPointType.ChamferEdgeLeft,
        loadOptions
      );

      this.addModificationPointGraphics(
        doorId,
        doorType,
        modificationPointsContainer,
        dimension,
        doorOpening,
        ModificationPointType.ChamferEdgeRight,
        loadOptions
      );
    }

    container.addChild(modificationPointsContainer);
  }

  private addModificationPointGraphics(
    doorId: number,
    doorType: DoorType,
    container: Container,
    dimension: Dimension2D,
    doorOpening: DoorOpening,
    modificationPointType: ModificationPointType,
    loadOptions: LoadDoorOptions
  ): void {
    const modificationLabel = Mapper.ModificationPointTypeToString(
      modificationPointType
    );

    if (!modificationLabel) {
      return;
    }

    const doorPoint: DoorPoint = DoorOpening.getDoorPoint(
      doorOpening,
      modificationPointType
    );

    const modificationPointGraphics = new Graphics();
    modificationPointGraphics.label = modificationLabel;
    modificationPointGraphics.interactive = true;
    modificationPointGraphics.interactiveChildren = false;
    modificationPointGraphics.cursor = Cursors.Pointer;
    modificationPointGraphics.eventMode = EventModes.Static;

    const xPosition =
      doorPoint.x + (this.pixiApplication.screen.width - dimension.width) / 2;
    const yPosition =
      doorPoint.y + (this.pixiApplication.screen.height - dimension.height) / 2;
    modificationPointGraphics.position.set(xPosition, yPosition);

    modificationPointGraphics
      .circle(0, 0, loadOptions.modificationPointRadius)
      .fill({
        color: loadOptions.modificationPointFillColor.color,
        alpha: loadOptions.modificationPointFillColor.alpha,
      })
      .stroke({
        color: loadOptions.modificationPointBorderColor.color,
        alpha: loadOptions.modificationPointBorderColor.alpha,
        width: loadOptions.modificationPointBorderWidth,
      });

    if (loadOptions.isModificationPointDraggable) {
      modificationPointGraphics.on(
        Events.PointerDown,
        (event: FederatedPointerEvent) => {
          event.preventDefault();
          event.stopPropagation();

          modificationPointGraphics.cursor = Cursors.Grabbing;

          if (!modificationPointType) {
            return;
          }

          this.selectDoorInternal(doorId, true);

          this.dragPointData = {
            target: modificationPointGraphics,
            doorId: doorId,
            modificationPointType: modificationPointType,
          };

          this.pixiApplication.stage.on(
            Events.PointerMove,
            this.onPointDragMove,
            this
          );

          if (
            this.loadDoorOptions.displayMagnification &&
            modificationPointType !== ModificationPointType.ArchEdgeLeft
          ) {
            this.magnificationContainer.visible = true;
            this.setMagnificationPosition(
              doorType,
              modificationPointType,
              event.global
            );
          }
        },
        this
      );
    }

    container.addChild(modificationPointGraphics);
  }

  private onDragEnd(): void {
    let hasUpdated = false;

    let doorId: number = -1;
    let modificationPointsContainer: Container = null;
    let updateArchPoint = false;

    if (this.dragDoorData) {
      const doorPerspectiveMesh: PerspectiveMesh =
        this.dragDoorData.target.getChildByLabel(
          Labels.DoorImage
        ) as PerspectiveMesh;

      if (doorPerspectiveMesh) {
        doorPerspectiveMesh.cursor = Cursors.Grab;
      }

      doorId = this.dragDoorData.doorId;
      modificationPointsContainer = this.dragDoorData.target.getChildByLabel(
        Labels.ModificationPoints
      ) as Container;

      this.dragDoorData = null;
      this.pixiApplication.stage.off(Events.PointerMove, this.onDoorDragMove);
      hasUpdated = true;
      updateArchPoint = true;
    }

    if (this.dragPointData) {
      const modificationPointGraphics: Graphics = this.dragPointData.target;

      if (modificationPointGraphics) {
        modificationPointGraphics.cursor = Cursors.Pointer;
      }

      if (
        this.loadDoorOptions.displayMagnification &&
        this.dragPointData.modificationPointType !==
          ModificationPointType.ArchEdgeLeft
      ) {
        this.magnificationContainer.visible = false;
      }

      doorId = this.dragPointData.doorId;
      modificationPointsContainer = this.dragPointData.target.parent;
      updateArchPoint =
        this.dragPointData.modificationPointType ===
          ModificationPointType.TopLeft ||
        this.dragPointData.modificationPointType ===
          ModificationPointType.BottomLeft;

      this.dragPointData = null;
      this.pixiApplication.stage.off(Events.PointerMove, this.onPointDragMove);
      hasUpdated = true;
    }

    if (hasUpdated) {
      const doorData = this.getDoorData(doorId);

      if (doorData && doorData.door.doorType === DoorType.ArchedGarageDoor) {
        const archEdgeLeftGraphics =
          modificationPointsContainer.getChildByLabel(
            Labels.ArchEdgeLeft
          ) as Graphics;

        const doorImagePerspectiveMesh =
          modificationPointsContainer.parent.getChildByLabel(
            Labels.DoorImage
          ) as PerspectiveMesh;

        if (archEdgeLeftGraphics && doorImagePerspectiveMesh) {
          archEdgeLeftGraphics.visible = true;

          if (updateArchPoint) {
            const hardwareImagePerspectiveMesh =
              modificationPointsContainer.parent.getChildByLabel(
                Labels.HardwareImage
              ) as PerspectiveMesh;

            const doorMaskImageContainer =
              modificationPointsContainer.parent.getChildByLabel(
                Labels.ArchDoorImageContainer
              ) as Container;

            const hardwareMaskImageContainer =
              modificationPointsContainer.parent.getChildByLabel(
                Labels.ArchHardwareImageContainer
              ) as Container;

            const previousDoorOpening = Calculator.calculateDoorOpeningSize(
              doorData.door,
              this.dimension
            );

            const previousDoorHeight =
              previousDoorOpening.bottomLeft.y - previousDoorOpening.topLeft.y;
            const previousDoorYOffset =
              previousDoorOpening.archEdgeLeft.y -
              previousDoorOpening.topLeft.y;

            const newDoorHeight =
              doorData.currentDoorOpening.bottomLeft.y -
              doorData.currentDoorOpening.topLeft.y;
            const newDoorYOffset =
              (previousDoorYOffset / previousDoorHeight) * newDoorHeight;

            const newDoorY =
              doorData.currentDoorOpening.topLeft.y + newDoorYOffset;
            const newDoorX =
              Calculator.calculateXOnLine(
                newDoorY,
                doorData.currentDoorOpening.topLeft,
                doorData.currentDoorOpening.bottomLeft
              ) + doorImagePerspectiveMesh.position.x;
            const archEdgeLeftPoint = new Point(newDoorX, newDoorY);

            this.moveDoorPoint(
              doorId,
              doorImagePerspectiveMesh,
              hardwareImagePerspectiveMesh,
              archEdgeLeftGraphics,
              archEdgeLeftPoint,
              ModificationPointType.ArchEdgeLeft,
              null, //chamferMaskGraphics
              doorMaskImageContainer,
              hardwareMaskImageContainer
            );
          }
        }
      }

      const updatedDoorOpening = Calculator.calculateDoorOpeningRatio(
        doorData.currentDoorOpening,
        this.dimension
      );

      doorData.door = {
        ...doorData.door,
        ...updatedDoorOpening,
      };

      this.emit(Events.Updated, this.getDoorOpenings());
    }
  }

  private onDoorDragMove(event: FederatedPointerEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.dragDoorData) {
      this.onDragEnd();
      return;
    }

    if (!this.dragDoorData.point) {
      this.onDragEnd();
      return;
    }

    if (this.endDragWhenOutOfImage) {
      const doorImagePerspectiveMesh: PerspectiveMesh =
        this.dragDoorData.target.getChildByLabel(
          Labels.DoorImage
        ) as PerspectiveMesh;

      if (!doorImagePerspectiveMesh) {
        this.onDragEnd();
        return;
      }

      const localPoint = doorImagePerspectiveMesh.toLocal(event.global);
      const doorImageContainsPoint =
        doorImagePerspectiveMesh.containsPoint(localPoint);

      if (!doorImageContainsPoint) {
        this.onDragEnd();
        return;
      }
    }

    if (
      event.global.x === this.dragDoorData.point.x &&
      event.global.y === this.dragDoorData.point.y
    ) {
      return;
    }

    const modificationPointsContainer =
      this.dragDoorData.target.getChildByLabel(
        Labels.ModificationPoints
      ) as Container;
    const archEdgeLeftGraphics = modificationPointsContainer.getChildByLabel(
      Labels.ArchEdgeLeft
    ) as Graphics;

    if (archEdgeLeftGraphics) {
      archEdgeLeftGraphics.visible = false;
    }

    const tempPoint = this.dragDoorData.point.clone();

    const tempX = event.global.x - tempPoint.x;
    const tempY = event.global.y - tempPoint.y;

    const differencePoint = new DoorPoint(tempX, tempY);

    if (!this.isDoorValidMove(this.dragDoorData.target, differencePoint)) {
      return;
    }

    this.dragDoorData.point = new Point(event.global.x, event.global.y);

    this.selectDoorInternal(this.dragDoorData.doorId, true);

    this.moveDoor(
      this.dragDoorData.doorId,
      this.dragDoorData.target,
      differencePoint
    );
  }

  private onPointDragMove(event: FederatedPointerEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.dragPointData) {
      return;
    }

    if (
      this.dragPointData.modificationPointType !==
      ModificationPointType.ArchEdgeLeft
    ) {
      const archEdgeLeftGraphics =
        this.dragPointData.target.parent.getChildByLabel(
          Labels.ArchEdgeLeft
        ) as Graphics;

      if (archEdgeLeftGraphics) {
        archEdgeLeftGraphics.visible = false;
      }
    }

    const doorImagePerspectiveMesh: PerspectiveMesh =
      this.dragPointData.target.parent.parent.getChildByLabel(
        Labels.DoorImage
      ) as PerspectiveMesh;

    const normalizedPoint: Point = this.normalizePoint(
      event.global,
      this.dragPointData.modificationPointType,
      this.dragPointData.doorId,
      doorImagePerspectiveMesh
    );

    const doorData = this.getDoorData(this.dragPointData.doorId);

    if (doorData.door.doorType === DoorType.ChamferedGarageDoor) {
      if (!this.isChamferPointValidMove(normalizedPoint, this.dragPointData)) {
        return;
      }
    } else {
      if (!this.isPointValidMove(normalizedPoint, this.dragPointData)) {
        return;
      }
    }

    const hardwareImagePerspectiveMesh: PerspectiveMesh =
      this.dragPointData.target.parent.parent.getChildByLabel(
        Labels.HardwareImage
      ) as PerspectiveMesh;

    const doorPerimeterGraphics: Graphics =
      this.dragPointData.target.parent.parent.getChildByLabel(
        Labels.DoorPerimeter
      ) as Graphics;

    const chamferMaskGraphics: Graphics =
      this.dragPointData.target.parent.parent.getChildByLabel(
        Labels.ChamferMask
      ) as Graphics;

    const archDoorImageContainer =
      this.dragPointData.target.parent.parent.getChildByLabel(
        Labels.ArchDoorImageContainer
      ) as Container;

    const archHardwareImageContainer =
      this.dragPointData.target.parent.parent.getChildByLabel(
        Labels.ArchHardwareImageContainer
      ) as Container;

    this.selectDoorInternal(this.dragPointData.doorId, true);

    this.moveDoorPoint(
      this.dragPointData.doorId,
      doorImagePerspectiveMesh,
      hardwareImagePerspectiveMesh,
      this.dragPointData.target,
      normalizedPoint,
      this.dragPointData.modificationPointType,
      chamferMaskGraphics,
      archDoorImageContainer,
      archHardwareImageContainer
    );

    if (doorPerimeterGraphics) {
      this.redrawDoorPerimeter(doorPerimeterGraphics, doorData);
    }

    if (
      this.loadDoorOptions.displayMagnification &&
      this.dragPointData.modificationPointType !==
        ModificationPointType.ArchEdgeLeft
    ) {
      this.magnificationContainer.visible = true;
      this.setMagnificationPosition(
        doorData.door.doorType,
        this.dragPointData.modificationPointType,
        event.global
      );
    }
  }

  private normalizePoint(
    point: Point,
    modificationPointType: ModificationPointType,
    doorId: number,
    perspectiveMesh: PerspectiveMesh
  ): Point {
    if (modificationPointType !== ModificationPointType.ArchEdgeLeft) {
      return point;
    }

    const normalizedPoint = point.clone();

    const doorData = this.getDoorData(doorId);

    if (!doorData) {
      return normalizedPoint;
    }

    if (normalizedPoint.y < doorData.currentDoorOpening.topLeft.y) {
      normalizedPoint.y =
        doorData.currentDoorOpening.topLeft.y + perspectiveMesh.position.y;
    }

    if (normalizedPoint.y > doorData.currentDoorOpening.bottomLeft.y) {
      normalizedPoint.y =
        doorData.currentDoorOpening.bottomLeft.y + perspectiveMesh.position.y;
    }

    normalizedPoint.x =
      Calculator.calculateXOnLine(
        normalizedPoint.y,
        doorData.currentDoorOpening.topLeft,
        doorData.currentDoorOpening.bottomLeft
      ) + perspectiveMesh.position.x;

    return normalizedPoint;
  }

  private redrawDoorPerimeter(
    doorPerimeterGraphics: Graphics,
    doorData: DoorData
  ): void {
    doorPerimeterGraphics.clear();

    const loadDoorOptions = this.getDoorData(doorData.doorId)?.loadDoorOptions;

    let strokeInput: StrokeInput = {
      color: loadDoorOptions?.perimeterColor.color,
      alpha: loadDoorOptions?.perimeterColor.alpha,
      width: loadDoorOptions?.perimeterWidth,
    };

    if (this.isDoorSelected(doorData.doorId)) {
      strokeInput = {
        color: loadDoorOptions.perimeterSelectedColor.color,
        alpha: loadDoorOptions.perimeterSelectedColor.alpha,
        width: loadDoorOptions.perimeterSelectedWidth,
      };
    }

    if (strokeInput.width <= 0) {
      return;
    }

    if (doorData.door.doorType === DoorType.ChamferedGarageDoor) {
      doorPerimeterGraphics
        .poly([
          doorData.currentDoorOpening.topLeft.x,
          doorData.currentDoorOpening.topLeft.y,
          doorData.currentDoorOpening.chamferEdgeLeft.x,
          doorData.currentDoorOpening.chamferEdgeLeft.y,
          doorData.currentDoorOpening.chamferEdgeRight.x,
          doorData.currentDoorOpening.chamferEdgeRight.y,
          doorData.currentDoorOpening.topRight.x,
          doorData.currentDoorOpening.topRight.y,
          doorData.currentDoorOpening.bottomRight.x,
          doorData.currentDoorOpening.bottomRight.y,
          doorData.currentDoorOpening.bottomLeft.x,
          doorData.currentDoorOpening.bottomLeft.y,
        ])
        .stroke(strokeInput);
    } else {
      doorPerimeterGraphics
        .poly([
          doorData.currentDoorOpening.topLeft.x,
          doorData.currentDoorOpening.topLeft.y,
          doorData.currentDoorOpening.topRight.x,
          doorData.currentDoorOpening.topRight.y,
          doorData.currentDoorOpening.bottomRight.x,
          doorData.currentDoorOpening.bottomRight.y,
          doorData.currentDoorOpening.bottomLeft.x,
          doorData.currentDoorOpening.bottomLeft.y,
        ])
        .stroke(strokeInput);
    }
  }

  private redrawModificationPoints(
    modificationPointsContainer: Container,
    doorData: DoorData
  ): void {
    const isDoorSelected = this.isDoorSelected(doorData.doorId);

    let radius = doorData.loadDoorOptions.modificationPointRadius;
    let fillColor = doorData.loadDoorOptions.modificationPointFillColor;
    let borderColor = doorData.loadDoorOptions.modificationPointBorderColor;
    let borderWidth = doorData.loadDoorOptions.modificationPointBorderWidth;

    if (isDoorSelected) {
      radius = doorData.loadDoorOptions.modificationPointSelectedRadius;
      fillColor = doorData.loadDoorOptions.modificationPointSelectedFillColor;
      borderColor =
        doorData.loadDoorOptions.modificationPointSelectedBorderColor;
      borderWidth =
        doorData.loadDoorOptions.modificationPointSelectedBorderWidth;
    }

    const topLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.TopLeftPoint
      ) as Graphics;

    if (topLeftGraphics) {
      this.redrawModificationPoint(
        topLeftGraphics,
        radius,
        fillColor,
        borderColor,
        borderWidth
      );
    }

    const topRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.TopRightPoint
      ) as Graphics;

    if (topRightGraphics) {
      this.redrawModificationPoint(
        topRightGraphics,
        radius,
        fillColor,
        borderColor,
        borderWidth
      );
    }

    const bottomRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.BottomRightPoint
      ) as Graphics;

    if (bottomRightGraphics) {
      this.redrawModificationPoint(
        bottomRightGraphics,
        radius,
        fillColor,
        borderColor,
        borderWidth
      );
    }

    const bottomLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.BottomLeftPoint
      ) as Graphics;

    if (bottomLeftGraphics) {
      this.redrawModificationPoint(
        bottomLeftGraphics,
        radius,
        fillColor,
        borderColor,
        borderWidth
      );
    }

    const archEdgeLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.ArchEdgeLeft
      ) as Graphics;

    if (archEdgeLeftGraphics) {
      let archedRadius = doorData.loadDoorOptions.modificationPointArchedRadius;
      let archedFillColor =
        doorData.loadDoorOptions.modificationPointArchedFillColor;
      let archedBorderColor =
        doorData.loadDoorOptions.modificationPointArchedBorderColor;
      let archedBorderWidth =
        doorData.loadDoorOptions.modificationPointArchedBorderWidth;

      if (isDoorSelected) {
        archedRadius =
          doorData.loadDoorOptions.modificationPointSelectedArchedRadius;
        archedFillColor =
          doorData.loadDoorOptions.modificationPointSelectedArchedFillColor;
        archedBorderColor =
          doorData.loadDoorOptions.modificationPointSelectedArchedBorderColor;
        archedBorderWidth =
          doorData.loadDoorOptions.modificationPointSelectedArchedBorderWidth;
      }

      this.redrawModificationPoint(
        archEdgeLeftGraphics,
        archedRadius,
        archedFillColor,
        archedBorderColor,
        archedBorderWidth
      );
    }

    const chamferEdgeLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.ChamferEdgeLeft
      ) as Graphics;

    if (chamferEdgeLeftGraphics) {
      this.redrawModificationPoint(
        chamferEdgeLeftGraphics,
        radius,
        fillColor,
        borderColor,
        borderWidth
      );
    }

    const chamferEdgeRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.ChamferEdgeRight
      ) as Graphics;

    if (chamferEdgeRightGraphics) {
      this.redrawModificationPoint(
        chamferEdgeRightGraphics,
        radius,
        fillColor,
        borderColor,
        borderWidth
      );
    }
  }

  private redrawModificationPoint(
    modificationPointGraphics: Graphics,
    radius: number,
    fillColor: HexColor,
    borderColor: HexColor,
    borderWidth: number
  ) {
    modificationPointGraphics.clear();

    modificationPointGraphics
      .circle(0, 0, radius)
      .fill({
        color: fillColor.color,
        alpha: fillColor.alpha,
      })
      .stroke({
        color: borderColor.color,
        alpha: borderColor.alpha,
        width: borderWidth,
      });
  }

  private isPointWithinDomicileSprite(point: Point): boolean {
    const localPoint = this.domicileSprite.toLocal(point);

    return this.domicileSprite.containsPoint(localPoint);
  }

  private async moveDoorPoint(
    doorId: number,
    doorImagePerspectiveMesh: PerspectiveMesh,
    hardwareImagePerspectiveMesh: PerspectiveMesh,
    pointGraphics: Graphics,
    point: Point,
    modificationPointType: ModificationPointType,
    chamferMaskGraphics: Graphics,
    doorMaskImageContainer: Container,
    hardwareMaskImageContainer: Container
  ): Promise<void> {
    pointGraphics.position.copyFrom(point);

    const doorPoint: DoorPoint = new DoorPoint(
      point.x - doorImagePerspectiveMesh.position.x,
      point.y - doorImagePerspectiveMesh.position.y
    );

    const doorData = this.getDoorData(doorId);

    if (modificationPointType === ModificationPointType.TopLeft) {
      doorData.currentDoorOpening.topLeft = doorPoint;
    } else if (modificationPointType === ModificationPointType.TopRight) {
      doorData.currentDoorOpening.topRight = doorPoint;
    } else if (modificationPointType === ModificationPointType.BottomRight) {
      doorData.currentDoorOpening.bottomRight = doorPoint;
    } else if (modificationPointType === ModificationPointType.BottomLeft) {
      doorData.currentDoorOpening.bottomLeft = doorPoint;
    } else if (modificationPointType === ModificationPointType.ArchEdgeLeft) {
      doorData.currentDoorOpening.archEdgeLeft = doorPoint;
    } else if (
      modificationPointType === ModificationPointType.ChamferEdgeLeft
    ) {
      doorData.currentDoorOpening.chamferEdgeLeft = doorPoint;
    } else if (
      modificationPointType === ModificationPointType.ChamferEdgeRight
    ) {
      doorData.currentDoorOpening.chamferEdgeRight = doorPoint;
    }

    if (doorImagePerspectiveMesh) {
      if (modificationPointType === ModificationPointType.ArchEdgeLeft) {
        this.updateArchMaskTexture(
          doorData.currentDoorOpening,
          doorMaskImageContainer,
          doorImagePerspectiveMesh
        );
      } else {
        this.moveMeshPoint(
          doorData,
          doorImagePerspectiveMesh,
          doorPoint,
          modificationPointType
        );
      }
    }

    if (hardwareImagePerspectiveMesh) {
      if (modificationPointType === ModificationPointType.ArchEdgeLeft) {
        this.updateArchMaskTexture(
          doorData.currentDoorOpening,
          hardwareMaskImageContainer,
          hardwareImagePerspectiveMesh
        );
      } else {
        this.moveMeshPoint(
          doorData,
          hardwareImagePerspectiveMesh,
          doorPoint,
          modificationPointType
        );
      }
    }

    if (
      doorData.door.doorType === DoorType.ChamferedGarageDoor &&
      chamferMaskGraphics
    ) {
      this.redrawChamferedMask(
        doorData.currentDoorOpening,
        chamferMaskGraphics
      );
    }
  }

  private redrawChamferedMask(
    doorOpening: DoorOpening,
    chamferMaskGraphics: Graphics
  ): void {
    chamferMaskGraphics.clear();

    chamferMaskGraphics
      .moveTo(doorOpening.topLeft.x, doorOpening.topLeft.y)
      .lineTo(doorOpening.chamferEdgeLeft.x, doorOpening.chamferEdgeLeft.y)
      .lineTo(doorOpening.chamferEdgeRight.x, doorOpening.chamferEdgeRight.y)
      .lineTo(doorOpening.topRight.x, doorOpening.topRight.y)
      .lineTo(doorOpening.bottomRight.x, doorOpening.bottomRight.y)
      .lineTo(doorOpening.bottomLeft.x, doorOpening.bottomLeft.y)
      .lineTo(doorOpening.topLeft.x, doorOpening.topLeft.y)
      .fill({
        color: HexColor.White.color,
        alpha: HexColor.White.alpha,
      });
  }

  private updateArchMaskTexture(
    doorOpening: DoorOpening,
    maskImageContainer: Container,
    perspectiveMesh: PerspectiveMesh
  ): void {
    const maskGraphics: Graphics = maskImageContainer.getChildByLabel(
      Labels.ArchMask
    ) as Graphics;

    const archSprite: Sprite = maskImageContainer.getChildByLabel(
      Labels.ArchSprite
    ) as Sprite;

    this.updateArchMaskGraphics(
      doorOpening,
      maskGraphics,
      archSprite.width,
      archSprite.height
    );

    const updatedTexture =
      this.generateTextureFromImageContainer(maskImageContainer);

    perspectiveMesh.texture.destroy(true);
    perspectiveMesh.texture = updatedTexture;
  }

  private updateArchMaskGraphics(
    doorOpening: DoorOpening,
    maskGraphics: Graphics,
    width: number,
    height: number
  ): void {
    maskGraphics.clear();

    this.drawArchMaskGraphics(maskGraphics, doorOpening, width, height);
  }

  private isDoorValidMove(
    doorContainer: Container,
    doorPoint: DoorPoint
  ): boolean {
    const modificationPointsContainer: Container =
      doorContainer.getChildByLabel(Labels.ModificationPoints) as Container;

    const perspectiveMesh: PerspectiveMesh = doorContainer.getChildByLabel(
      Labels.DoorImage
    ) as PerspectiveMesh;

    const topLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.TopLeftPoint
      ) as Graphics;

    const topLeftPoint: Point = new Point(
      topLeftGraphics.x + doorPoint.x,
      topLeftGraphics.y + doorPoint.y
    );

    if (!this.isPointWithinBoundary(topLeftPoint, perspectiveMesh)) {
      return false;
    }

    const topRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.TopRightPoint
      ) as Graphics;

    const topRightPoint: Point = new Point(
      topRightGraphics.x + doorPoint.x,
      topRightGraphics.y + doorPoint.y
    );

    if (!this.isPointWithinBoundary(topRightPoint, perspectiveMesh)) {
      return false;
    }

    const bottomLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.BottomLeftPoint
      ) as Graphics;

    const bottomLeftPoint: Point = new Point(
      bottomLeftGraphics.x + doorPoint.x,
      bottomLeftGraphics.y + doorPoint.y
    );

    if (!this.isPointWithinBoundary(bottomLeftPoint, perspectiveMesh)) {
      return false;
    }

    const bottomRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.BottomRightPoint
      ) as Graphics;

    const bottomRightPoint: Point = new Point(
      bottomRightGraphics.x + doorPoint.x,
      bottomRightGraphics.y + doorPoint.y
    );

    if (!this.isPointWithinBoundary(bottomRightPoint, perspectiveMesh)) {
      return false;
    }

    const chamferEdgeLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.ChamferEdgeLeft
      ) as Graphics;

    if (chamferEdgeLeftGraphics) {
      const chamferEdgeLeftPoint: Point = new Point(
        chamferEdgeLeftGraphics.x + doorPoint.x,
        chamferEdgeLeftGraphics.y + doorPoint.y
      );

      if (!this.isPointWithinBoundary(chamferEdgeLeftPoint, perspectiveMesh)) {
        return false;
      }
    }

    const chamferEdgeRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.ChamferEdgeRight
      ) as Graphics;

    if (chamferEdgeRightGraphics) {
      const chamferEdgeRightPoint: Point = new Point(
        chamferEdgeRightGraphics.x + doorPoint.x,
        chamferEdgeRightGraphics.y + doorPoint.y
      );

      if (!this.isPointWithinBoundary(chamferEdgeRightPoint, perspectiveMesh)) {
        return false;
      }
    }

    return true;
  }

  private isPointWithinBoundary(
    point: Point,
    perspectiveMesh: PerspectiveMesh
  ): boolean {
    if (point.x < perspectiveMesh.x + this.marginDistance) {
      return false;
    }

    if (
      point.x >
      this.pixiApplication.screen.width -
        perspectiveMesh.x -
        this.marginDistance
    ) {
      return false;
    }

    if (point.y < perspectiveMesh.y + this.marginDistance) {
      return false;
    }

    if (
      point.y >
      this.pixiApplication.screen.height -
        perspectiveMesh.y -
        this.marginDistance
    ) {
      return false;
    }

    return true;
  }

  private isChamferPointValidMove(
    point: Point,
    dragPointData: DragPointData
  ): boolean {
    if (!this.isPointWithinDomicileSprite(point)) return false;

    const topLeft: Graphics = this.dragPointData.target.parent.getChildByLabel(
      Labels.TopLeftPoint
    ) as Graphics;

    const topRight: Graphics = this.dragPointData.target.parent.getChildByLabel(
      Labels.TopRightPoint
    ) as Graphics;

    const bottomLeft: Graphics =
      this.dragPointData.target.parent.getChildByLabel(
        Labels.BottomLeftPoint
      ) as Graphics;

    const bottomRight: Graphics =
      this.dragPointData.target.parent.getChildByLabel(
        Labels.BottomRightPoint
      ) as Graphics;

    const chamferEdgeLeft: Graphics =
      this.dragPointData.target.parent.getChildByLabel(
        Labels.ChamferEdgeLeft
      ) as Graphics;

    const chamferEdgeRight: Graphics =
      this.dragPointData.target.parent.getChildByLabel(
        Labels.ChamferEdgeRight
      ) as Graphics;

    if (dragPointData.modificationPointType === ModificationPointType.TopLeft) {
      return this.isChamferTopLeftMoveValid(
        bottomLeft,
        bottomRight,
        chamferEdgeLeft,
        chamferEdgeRight,
        point
      );
    }

    if (
      dragPointData.modificationPointType === ModificationPointType.TopRight
    ) {
      return this.isChamferTopRightMoveValid(
        bottomLeft,
        bottomRight,
        chamferEdgeLeft,
        chamferEdgeRight,
        point
      );
    }

    if (
      dragPointData.modificationPointType === ModificationPointType.BottomRight
    ) {
      return this.isChamferBottomRightMoveValid(
        topLeft,
        topRight,
        bottomLeft,
        chamferEdgeRight,
        point
      );
    }

    if (
      dragPointData.modificationPointType === ModificationPointType.BottomLeft
    ) {
      return this.isChamferBottomLeftMoveValid(
        topLeft,
        topRight,
        bottomRight,
        chamferEdgeLeft,
        point
      );
    }

    if (
      dragPointData.modificationPointType ===
      ModificationPointType.ChamferEdgeLeft
    ) {
      return this.isChamferEdgeLeftMoveValid(
        topLeft,
        topRight,
        bottomLeft,
        chamferEdgeRight,
        point
      );
    }

    if (
      dragPointData.modificationPointType ===
      ModificationPointType.ChamferEdgeRight
    ) {
      return this.isChamferEdgeRightMoveValid(
        topLeft,
        topRight,
        bottomRight,
        chamferEdgeLeft,
        point
      );
    }

    return true;
  }

  private isChamferTopLeftMoveValid(
    bottomLeft: Graphics,
    bottomRight: Graphics,
    chamferEdgeLeft: Graphics,
    chamferEdgeRight: Graphics,
    point: Point
  ): boolean {
    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      chamferEdgeRight,
      chamferEdgeLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomLeft,
      chamferEdgeLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomLeft,
      bottomRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isChamferTopRightMoveValid(
    bottomLeft: Graphics,
    bottomRight: Graphics,
    chamferEdgeLeft: Graphics,
    chamferEdgeRight: Graphics,
    point: Point
  ): boolean {
    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      chamferEdgeRight,
      chamferEdgeLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomLeft,
      bottomRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      chamferEdgeRight,
      bottomRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isChamferBottomRightMoveValid(
    topLeft: Graphics,
    topRight: Graphics,
    bottomLeft: Graphics,
    chamferEdgeRight: Graphics,
    point: Point
  ): boolean {
    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      topRight,
      bottomLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topRight,
      chamferEdgeRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topLeft,
      bottomLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isChamferBottomLeftMoveValid(
    topLeft: Graphics,
    topRight: Graphics,
    bottomRight: Graphics,
    chamferEdgeLeft: Graphics,
    point: Point
  ): boolean {
    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomRight,
      topLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      chamferEdgeLeft,
      topLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomRight,
      topRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isChamferEdgeLeftMoveValid(
    topLeft: Graphics,
    topRight: Graphics,
    bottomLeft: Graphics,
    chamferEdgeRight: Graphics,
    point: Point
  ): boolean {
    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      topLeft,
      bottomLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topLeft,
      chamferEdgeRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topRight,
      chamferEdgeRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isChamferEdgeRightMoveValid(
    topLeft: Graphics,
    topRight: Graphics,
    bottomRight: Graphics,
    chamferEdgeLeft: Graphics,
    point: Point
  ): boolean {
    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomRight,
      topRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      chamferEdgeLeft,
      topRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      chamferEdgeLeft,
      topLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isPointValidMove(
    point: Point,
    dragPointData: DragPointData
  ): boolean {
    if (!this.isPointWithinDomicileSprite(point)) return false;

    const topLeft: Graphics = this.dragPointData.target.parent.getChildByLabel(
      Labels.TopLeftPoint
    ) as Graphics;

    const topRight: Graphics = this.dragPointData.target.parent.getChildByLabel(
      Labels.TopRightPoint
    ) as Graphics;

    const bottomLeft: Graphics =
      this.dragPointData.target.parent.getChildByLabel(
        Labels.BottomLeftPoint
      ) as Graphics;

    const bottomRight: Graphics =
      this.dragPointData.target.parent.getChildByLabel(
        Labels.BottomRightPoint
      ) as Graphics;

    if (dragPointData.modificationPointType === ModificationPointType.TopLeft) {
      return this.isTopLeftMoveValid(topRight, bottomLeft, bottomRight, point);
    }

    if (
      dragPointData.modificationPointType === ModificationPointType.TopRight
    ) {
      return this.isTopRightMoveValid(topLeft, bottomLeft, bottomRight, point);
    }

    if (
      dragPointData.modificationPointType === ModificationPointType.BottomRight
    ) {
      return this.isBottomRightMoveValid(topLeft, topRight, bottomLeft, point);
    }

    if (
      dragPointData.modificationPointType === ModificationPointType.BottomLeft
    ) {
      return this.isBottomLeftMoveValid(topLeft, topRight, bottomRight, point);
    }

    return true;
  }

  private isTopLeftMoveValid(
    topRight: Graphics,
    bottomLeft: Graphics,
    bottomRight: Graphics,
    point: Point
  ): boolean {
    if (bottomLeft.y - point.y < this.minimumDistance) {
      return false;
    }

    if (topRight.x - point.x < this.minimumDistance) {
      return false;
    }

    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomLeft,
      topRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomLeft,
      bottomRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomRight,
      topRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isTopRightMoveValid(
    topLeft: Graphics,
    bottomLeft: Graphics,
    bottomRight: Graphics,
    point: Point
  ): boolean {
    if (bottomRight.y - point.y < this.minimumDistance) {
      return false;
    }

    if (point.x - topLeft.x < this.minimumDistance) {
      return false;
    }

    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      topLeft,
      bottomRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomLeft,
      bottomRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topLeft,
      bottomLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isBottomRightMoveValid(
    topLeft: Graphics,
    topRight: Graphics,
    bottomLeft: Graphics,
    point: Point
  ): boolean {
    if (point.x - bottomLeft.x < this.minimumDistance) {
      return false;
    }

    if (point.y - topRight.y < this.minimumDistance) {
      return false;
    }

    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      topRight,
      bottomLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topRight,
      topLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topLeft,
      bottomLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private isBottomLeftMoveValid(
    topLeft: Graphics,
    topRight: Graphics,
    bottomRight: Graphics,
    point: Point
  ): boolean {
    if (bottomRight.x - point.x < this.minimumDistance) {
      return false;
    }

    if (point.y - topLeft.y < this.minimumDistance) {
      return false;
    }

    let distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomRight,
      topLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      topRight,
      topLeft,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    distance = Calculator.calculateDistanceBetweenPointAndLine(
      bottomRight,
      topRight,
      point
    );

    if (distance < this.minimumDistance) {
      return false;
    }

    return true;
  }

  private moveDoor(
    doorId: number,
    doorContainer: Container,
    doorPoint: DoorPoint
  ): void {
    const doorImagePerspectiveMesh: PerspectiveMesh =
      doorContainer.getChildByLabel(Labels.DoorImage) as PerspectiveMesh;

    const hardwareImagePerspectiveMesh: PerspectiveMesh =
      doorContainer.getChildByLabel(Labels.HardwareImage) as PerspectiveMesh;

    const chamferMaskGraphics: Graphics = doorContainer.getChildByLabel(
      Labels.ChamferMask
    ) as Graphics;

    const archDoorImageContainer: Container = doorContainer.getChildByLabel(
      Labels.ArchDoorImageContainer
    ) as Container;

    const archHardwareImageContainer: Container = doorContainer.getChildByLabel(
      Labels.ArchHardwareImageContainer
    ) as Container;

    const modificationPointsContainer: Container =
      doorContainer.getChildByLabel(Labels.ModificationPoints) as Container;

    const topLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.TopLeftPoint
      ) as Graphics;

    const topLeftPoint: Point = new Point(
      topLeftGraphics.x + doorPoint.x,
      topLeftGraphics.y + doorPoint.y
    );

    this.moveDoorPoint(
      doorId,
      doorImagePerspectiveMesh,
      hardwareImagePerspectiveMesh,
      topLeftGraphics,
      topLeftPoint,
      ModificationPointType.TopLeft,
      chamferMaskGraphics,
      archDoorImageContainer,
      archHardwareImageContainer
    );

    const topRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.TopRightPoint
      ) as Graphics;

    const topRightPoint: Point = new Point(
      topRightGraphics.x + doorPoint.x,
      topRightGraphics.y + doorPoint.y
    );

    this.moveDoorPoint(
      doorId,
      doorImagePerspectiveMesh,
      hardwareImagePerspectiveMesh,
      topRightGraphics,
      topRightPoint,
      ModificationPointType.TopRight,
      chamferMaskGraphics,
      archDoorImageContainer,
      archHardwareImageContainer
    );

    const bottomLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.BottomLeftPoint
      ) as Graphics;

    const bottomLeftPoint: Point = new Point(
      bottomLeftGraphics.x + doorPoint.x,
      bottomLeftGraphics.y + doorPoint.y
    );

    this.moveDoorPoint(
      doorId,
      doorImagePerspectiveMesh,
      hardwareImagePerspectiveMesh,
      bottomLeftGraphics,
      bottomLeftPoint,
      ModificationPointType.BottomLeft,
      chamferMaskGraphics,
      archDoorImageContainer,
      archHardwareImageContainer
    );

    const bottomRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.BottomRightPoint
      ) as Graphics;

    const bottomRightPoint: Point = new Point(
      bottomRightGraphics.x + doorPoint.x,
      bottomRightGraphics.y + doorPoint.y
    );

    this.moveDoorPoint(
      doorId,
      doorImagePerspectiveMesh,
      hardwareImagePerspectiveMesh,
      bottomRightGraphics,
      bottomRightPoint,
      ModificationPointType.BottomRight,
      chamferMaskGraphics,
      archDoorImageContainer,
      archHardwareImageContainer
    );

    const chamferEdgeLeftGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.ChamferEdgeLeft
      ) as Graphics;

    if (chamferEdgeLeftGraphics) {
      const chamferEdgeLeftPoint: Point = new Point(
        chamferEdgeLeftGraphics.x + doorPoint.x,
        chamferEdgeLeftGraphics.y + doorPoint.y
      );

      this.moveDoorPoint(
        doorId,
        doorImagePerspectiveMesh,
        hardwareImagePerspectiveMesh,
        chamferEdgeLeftGraphics,
        chamferEdgeLeftPoint,
        ModificationPointType.ChamferEdgeLeft,
        chamferMaskGraphics,
        archDoorImageContainer,
        archHardwareImageContainer
      );
    }

    const chamferEdgeRightGraphics: Graphics =
      modificationPointsContainer.getChildByLabel(
        Labels.ChamferEdgeRight
      ) as Graphics;

    if (chamferEdgeRightGraphics) {
      const chamferEdgeRightPoint: Point = new Point(
        chamferEdgeRightGraphics.x + doorPoint.x,
        chamferEdgeRightGraphics.y + doorPoint.y
      );

      this.moveDoorPoint(
        doorId,
        doorImagePerspectiveMesh,
        hardwareImagePerspectiveMesh,
        chamferEdgeRightGraphics,
        chamferEdgeRightPoint,
        ModificationPointType.ChamferEdgeRight,
        chamferMaskGraphics,
        archDoorImageContainer,
        archHardwareImageContainer
      );
    }

    if (chamferMaskGraphics) {
      this.redrawChamferedMask(
        this.getDoorData(doorId).currentDoorOpening,
        chamferMaskGraphics
      );
    }

    const doorPerimeterGraphics: Graphics = doorContainer.getChildByLabel(
      Labels.DoorPerimeter
    ) as Graphics;

    if (doorPerimeterGraphics) {
      this.redrawDoorPerimeter(doorPerimeterGraphics, this.getDoorData(doorId));
    }
  }

  private async moveMeshPoint(
    doorData: DoorData,
    perspectiveMesh: PerspectiveMesh,
    doorPoint: DoorPoint,
    modificationPointType: ModificationPointType
  ): Promise<void> {
    if (doorData.door.doorType === DoorType.ChamferedGarageDoor) {
      const chamferEdgeLine = Calculator.calculateLineFormula(
        doorData.currentDoorOpening.chamferEdgeLeft,
        doorData.currentDoorOpening.chamferEdgeRight
      );

      const leftLine = Calculator.calculateLineFormula(
        doorData.currentDoorOpening.topLeft,
        doorData.currentDoorOpening.bottomLeft
      );

      const rightLine = Calculator.calculateLineFormula(
        doorData.currentDoorOpening.topRight,
        doorData.currentDoorOpening.bottomRight
      );

      const topLeftPoint = Calculator.calculateLinesIntersection(
        leftLine,
        chamferEdgeLine
      );

      const topRightPoint = Calculator.calculateLinesIntersection(
        rightLine,
        chamferEdgeLine
      );

      try {
        if (modificationPointType === ModificationPointType.BottomRight) {
          perspectiveMesh.setCorners(
            topLeftPoint.x,
            topLeftPoint.y,
            topRightPoint.x,
            topRightPoint.y,
            doorPoint.x,
            doorPoint.y,
            perspectiveMesh.geometry.corners[6],
            perspectiveMesh.geometry.corners[7]
          );
        } else if (modificationPointType === ModificationPointType.BottomLeft) {
          perspectiveMesh.setCorners(
            topLeftPoint.x,
            topLeftPoint.y,
            topRightPoint.x,
            topRightPoint.y,
            perspectiveMesh.geometry.corners[4],
            perspectiveMesh.geometry.corners[5],
            doorPoint.x,
            doorPoint.y
          );
        } else {
          perspectiveMesh.setCorners(
            topLeftPoint.x,
            topLeftPoint.y,
            topRightPoint.x,
            topRightPoint.y,
            perspectiveMesh.geometry.corners[4],
            perspectiveMesh.geometry.corners[5],
            perspectiveMesh.geometry.corners[6],
            perspectiveMesh.geometry.corners[7]
          );
        }
      } catch (error) {
        console.error("Error occurred while moving mesh point:", error);
      }

      return;
    }

    if (modificationPointType === ModificationPointType.TopLeft) {
      perspectiveMesh.setCorners(
        doorPoint.x,
        doorPoint.y,
        perspectiveMesh.geometry.corners[2],
        perspectiveMesh.geometry.corners[3],
        perspectiveMesh.geometry.corners[4],
        perspectiveMesh.geometry.corners[5],
        perspectiveMesh.geometry.corners[6],
        perspectiveMesh.geometry.corners[7]
      );
    } else if (modificationPointType === ModificationPointType.TopRight) {
      perspectiveMesh.setCorners(
        perspectiveMesh.geometry.corners[0],
        perspectiveMesh.geometry.corners[1],
        doorPoint.x,
        doorPoint.y,
        perspectiveMesh.geometry.corners[4],
        perspectiveMesh.geometry.corners[5],
        perspectiveMesh.geometry.corners[6],
        perspectiveMesh.geometry.corners[7]
      );
    } else if (modificationPointType === ModificationPointType.BottomRight) {
      perspectiveMesh.setCorners(
        perspectiveMesh.geometry.corners[0],
        perspectiveMesh.geometry.corners[1],
        perspectiveMesh.geometry.corners[2],
        perspectiveMesh.geometry.corners[3],
        doorPoint.x,
        doorPoint.y,
        perspectiveMesh.geometry.corners[6],
        perspectiveMesh.geometry.corners[7]
      );
    } else if (modificationPointType === ModificationPointType.BottomLeft) {
      perspectiveMesh.setCorners(
        perspectiveMesh.geometry.corners[0],
        perspectiveMesh.geometry.corners[1],
        perspectiveMesh.geometry.corners[2],
        perspectiveMesh.geometry.corners[3],
        perspectiveMesh.geometry.corners[4],
        perspectiveMesh.geometry.corners[5],
        doorPoint.x,
        doorPoint.y
      );
    }
  }

  private deselectDoorUI(doorId: number): void {
    const doorData = this.getDoorData(doorId);

    if (doorData) {
      const doorPerimeterGraphics: Graphics =
        doorData.container.getChildByLabel(Labels.DoorPerimeter) as Graphics;

      if (doorPerimeterGraphics) {
        this.redrawDoorPerimeter(doorPerimeterGraphics, doorData);
      }

      const modificationPointsContainer: Container =
        doorData.container.getChildByLabel(
          Labels.ModificationPoints
        ) as Container;

      if (modificationPointsContainer) {
        this.redrawModificationPoints(modificationPointsContainer, doorData);
      }
    }
  }

  private selectDoorUI(doorId: number): void {
    if (doorId < 0) {
      this.selectedDoorId = -1;
      return;
    }

    const doorData = this.getDoorData(doorId);

    if (doorData) {
      this.selectedDoorId = doorId;

      const highestZIndex = this.doorsData.length - 1;

      if (doorData.container.zIndex !== highestZIndex) {
        doorData.container.zIndex = highestZIndex;

        this.doorsData.forEach((otherDoorData) => {
          if (otherDoorData.doorId !== doorId) {
            otherDoorData.container.zIndex = otherDoorData.container.zIndex - 1;
          }
        });
      }

      const modificationPointsContainer: Container =
        doorData.container.getChildByLabel(
          Labels.ModificationPoints
        ) as Container;

      if (modificationPointsContainer) {
        this.redrawModificationPoints(modificationPointsContainer, doorData);
      }

      const doorPerimeterGraphics: Graphics =
        doorData.container.getChildByLabel(Labels.DoorPerimeter) as Graphics;

      if (doorPerimeterGraphics) {
        this.redrawDoorPerimeter(doorPerimeterGraphics, doorData);
      }
    }
  }

  public getDoors(): Door[] {
    return this.doorsData.map((doorData) => {
      return {
        ...doorData.door,
        ...Calculator.calculateDoorOpeningRatio(
          doorData.currentDoorOpening,
          this.dimension
        ),
      };
    });
  }

  public getDoorIds(): number[] {
    return this.doorsData.map((doorData) => doorData.doorId);
  }

  public getFirstDoorId(): number | undefined {
    if (this.doorsData.length === 0) {
      return undefined;
    }

    const doorData = this.doorsData[0];
    return doorData.doorId;
  }

  public getLastDoorId(): number | undefined {
    if (this.doorsData.length === 0) {
      return undefined;
    }

    const doorData = this.doorsData[this.doorsData.length - 1];
    return doorData.doorId;
  }

  public clearDoors(): void {
    this.doorsContainer.removeChildren(0, this.doorsContainer.children.length);
    this.doorsData = [];
    this.selectedDoorId = -1;
  }

  public async loadSingleDoorFromUrl(
    singleDoorOptions: SingleDoorOptions
  ): Promise<void> {
    this.clearDomicile();
    this.clearDoors();

    const door: Door = {
      imageUrl: singleDoorOptions.doorImageUrl,
      doorType: DoorType.QuadrilateralGarageDoor,
      topLeft: {
        x: 0,
        y: 0,
      },
      topRight: {
        x: 1,
        y: 0,
      },
      bottomRight: {
        x: 1,
        y: 1,
      },
      bottomLeft: {
        x: 0,
        y: 1,
      },
    };

    if (singleDoorOptions.hardwareImageUrl) {
      door.hardware = new Hardware(singleDoorOptions.hardwareImageUrl);
    }

    this.addDoor(
      door,
      new Dimension2D(
        this.pixiApplication.screen.width,
        this.pixiApplication.screen.height
      ),
      {
        displayPerimeter: false,
        displayModificationPoints: false,
        isModificationPointDraggable: false,
        isDoorDraggable: false,
      }
    );
  }

  public async loadDoorOpeningsFromBlob(
    content: Blob,
    doorOpenings: DoorOpening[],
    loadOptions: LoadDoorOpeningsOptions
  ): Promise<void> {
    this.dimension = await this.loadDomicileFromBlob(
      content,
      loadOptions.aspect
    );
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoorOpenings(this.dimension, doorOpenings, loadOptions);
  }

  public async loadDoorOpeningsFromUrl(
    url: string,
    doorOpenings: DoorOpening[],
    loadOptions: LoadDoorOpeningsOptions
  ): Promise<void> {
    this.dimension = await this.loadDomicileFromUrl(url, loadOptions.aspect);
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoorOpenings(this.dimension, doorOpenings, loadOptions);
  }

  public async loadDoorOpeningsFromByteArray(
    uint8Array: Uint8Array,
    doorOpenings: DoorOpening[],
    loadOptions: LoadDoorOpeningsOptions
  ): Promise<void> {
    this.dimension = await this.loadDomicileFromByteArray(
      uint8Array,
      loadOptions.aspect
    );
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoorOpenings(this.dimension, doorOpenings, loadOptions);
  }

  public async loadDoorOpeningsFromFile(
    file: File,
    doorOpenings: DoorOpening[],
    loadOptions: LoadDoorOpeningsOptions
  ) {
    this.dimension = await this.loadDomicileFromFile(file, loadOptions.aspect);
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoorOpenings(this.dimension, doorOpenings, loadOptions);
  }

  private async loadDoorOpenings(
    dimension: Dimension2D,
    doorOpenings: DoorOpening[],
    loadDoorOpeningsOptions: LoadDoorOpeningsOptions
  ): Promise<void> {
    this.clearDoors();

    if (
      doorOpenings === undefined ||
      doorOpenings === null ||
      doorOpenings.length === 0
    ) {
      return;
    }

    this.loadDoorOptions = {
      ...Calculator.generateDefaultLoadDoorOptions(),
      ...loadDoorOpeningsOptions.defaultLoadDoorOptions,
    };

    for (const doorOpening of doorOpenings) {
      let hardware: Hardware | undefined = undefined;

      if (loadDoorOpeningsOptions.defaultHardwareImageUrl !== undefined) {
        hardware = new Hardware(
          loadDoorOpeningsOptions.defaultHardwareImageUrl
        );
      }

      const door: Door = {
        imageUrl: loadDoorOpeningsOptions.defaultDoorImageUrl,
        doorType: doorOpening.doorType,
        topLeft: doorOpening.topLeft,
        topRight: doorOpening.topRight,
        bottomRight: doorOpening.bottomRight,
        bottomLeft: doorOpening.bottomLeft,
        chamferEdgeLeft: doorOpening.chamferEdgeLeft,
        chamferEdgeRight: doorOpening.chamferEdgeRight,
        archEdgeLeft: doorOpening.archEdgeLeft,
        hardware,
      };

      await this.addDoor(door, dimension, this.loadDoorOptions);
    }

    this.addModificationMagnificationGraphics(this.loadDoorOptions);
    this.selectDoorAfterLoad(loadDoorOpeningsOptions);
  }

  private selectDoorAfterLoad(
    loadDoorOpeningsOptions: LoadDoorOpeningsOptions
  ) {
    let selectedDoorId = -1;

    if (
      loadDoorOpeningsOptions.selectedDoorIndex !== undefined &&
      loadDoorOpeningsOptions.selectedDoorIndex !== null &&
      loadDoorOpeningsOptions.selectedDoorIndex >= 0 &&
      loadDoorOpeningsOptions.selectedDoorIndex < this.doorsData.length
    ) {
      selectedDoorId =
        this.doorsData[loadDoorOpeningsOptions.selectedDoorIndex].doorId;
    }

    if (!this.loadDoorOptions.allowNoDoorsSelected && selectedDoorId === -1) {
      selectedDoorId = this.doorsData[0].doorId;
    }

    if (selectedDoorId !== -1) {
      this.selectDoorInternal(selectedDoorId, false);
    }
  }

  public async loadDoorsFromUrl(
    url: string,
    doors: Door[],
    loadOptions: LoadDoorOpeningsOptions
  ): Promise<void> {
    this.dimension = await this.loadDomicileFromUrl(url, loadOptions.aspect);
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoors(this.dimension, doors, loadOptions);
  }

  public async loadDoorsFromBlob(
    content: Blob,
    doors: Door[],
    loadOptions: LoadDoorOpeningsOptions
  ) {
    this.dimension = await this.loadDomicileFromBlob(
      content,
      loadOptions.aspect
    );
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoors(this.dimension, doors, loadOptions);
  }

  public async loadDoorsFromByteArray(
    uint8Array: Uint8Array,
    doors: Door[],
    loadOptions: LoadDoorOpeningsOptions
  ): Promise<void> {
    this.dimension = await this.loadDomicileFromByteArray(
      uint8Array,
      loadOptions.aspect
    );
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoors(this.dimension, doors, loadOptions);
  }

  public async loadDoorsFromFile(
    file: File,
    doors: Door[],
    loadOptions: LoadDoorOpeningsOptions
  ) {
    this.dimension = await this.loadDomicileFromFile(file, loadOptions.aspect);
    this.setDomicilePositionByTransparent(loadOptions.transparentOpenings);
    await this.loadDoors(this.dimension, doors, loadOptions);
  }

  private async loadDoors(
    dimension: Dimension2D,
    doors: Door[],
    loadDoorOpeningsOptions: LoadDoorOpeningsOptions
  ): Promise<void> {
    this.clearDoors();

    if (doors === undefined || doors === null || doors.length === 0) {
      return;
    }

    this.loadDoorOptions = {
      ...Calculator.generateDefaultLoadDoorOptions(),
      ...loadDoorOpeningsOptions.defaultLoadDoorOptions,
    };

    for (const door of doors) {
      await this.addDoor(door, dimension, this.loadDoorOptions);
    }

    this.addModificationMagnificationGraphics(this.loadDoorOptions);
    this.selectDoorAfterLoad(loadDoorOpeningsOptions);
  }

  public getDoorOpenings(): DoorOpening[] {
    return this.doorsData.map((doorData) => {
      return Calculator.calculateDoorOpeningRatio(
        doorData.currentDoorOpening,
        this.dimension
      );
    });
  }

  public getSelectedDoorId(): number {
    return this.selectedDoorId;
  }

  private getDoorData(doorId: number): DoorData | undefined {
    return this.doorsData.find((doorData) => doorData.doorId === doorId);
  }

  public getDoor(doorId: number): Door | undefined {
    const doorData = this.getDoorData(doorId);

    if (!doorData) {
      return undefined;
    }

    return {
      ...doorData.door,
      ...Calculator.calculateDoorOpeningRatio(
        doorData.currentDoorOpening,
        this.dimension
      ),
    };
  }

  public getSelectedDoor(): Door | undefined {
    const doorId = this.getSelectedDoorId();

    if (doorId < 0) {
      return undefined;
    }

    return this.getDoor(doorId);
  }

  public setSelectedDoor(doorId: number): void {
    this.selectDoorInternal(doorId, false);
  }

  public isDoorSelected(doorId: number): boolean {
    return this.selectedDoorId === doorId;
  }

  private selectDoorInternal(doorId: number, raiseEvent: boolean) {
    if (doorId === -1 && !this.loadDoorOptions.allowNoDoorsSelected) {
      return;
    }

    if (this.selectedDoorId !== doorId) {
      const previousSelectedDoorId = this.selectedDoorId;
      this.selectDoorUI(doorId);

      if (previousSelectedDoorId >= 0) {
        this.deselectDoorUI(previousSelectedDoorId);
      }

      if (raiseEvent) {
        this.emit(Events.SelectionUpdated, doorId);
      }
    }
  }

  public removeDoor(doorId: number): void {
    const doorData = this.getDoorData(doorId);

    if (!doorData) {
      return;
    }

    this.doorsContainer.removeChild(doorData.container);
    this.doorsData = this.doorsData.filter((data) => data.doorId !== doorId);

    if (this.isDoorSelected(doorId)) {
      this.selectedDoorId = -1;
    }

    if (
      !this.loadDoorOptions.allowNoDoorsSelected &&
      this.doorsData.length > 0
    ) {
      this.selectDoorInternal(this.doorsData[0].doorId, false);
      return;
    }

    this.emit(Events.SelectionUpdated, this.selectedDoorId);
  }

  public rotateDomicileImage(
    rotationAngle: RotationAngle,
    clockwise: boolean
  ): void {
    let rotate = this.domicileSprite.texture.rotate;

    rotate = Calculator.calculateRotateValue(rotate, rotationAngle, clockwise);

    this.rotateDomicileImageInternal(rotate);
  }

  public flipDomicileImage(vertically: boolean) {
    let rotate = this.domicileSprite.texture.rotate;

    rotate = Calculator.calculateFlipValue(rotate, vertically);

    this.rotateDomicileImageInternal(rotate);
  }

  private rotateDomicileImageInternal(rotate: number) {
    const D8 = groupD8;
    const originalTexture = this.domicileSprite.texture;

    const originalTextureFrame = originalTexture.frame;
    const source = originalTexture.source;

    const height = D8.isVertical(rotate)
      ? originalTextureFrame.width
      : originalTextureFrame.height;
    const width = D8.isVertical(rotate)
      ? originalTextureFrame.height
      : originalTextureFrame.width;

    const texture = new Texture({
      source,
      rotate,
    });

    this.domicileSprite.texture = texture;
    this.domicileSprite.width = width;
    this.domicileSprite.height = height;

    const aspectRatio = new Dimension2D(width, height);

    const boundingSize = new Dimension2D(
      this.pixiApplication.screen.width,
      this.pixiApplication.screen.height
    );

    const aspectRatioDimension = Calculator.calculateAspectRatio(
      this.aspect,
      aspectRatio,
      boundingSize
    );

    this.domicileSprite.x =
      (this.pixiApplication.screen.width - aspectRatioDimension.width) / 2;
    this.domicileSprite.y =
      (this.pixiApplication.screen.height - aspectRatioDimension.height) / 2;
    this.domicileSprite.width = aspectRatioDimension.width;
    this.domicileSprite.height = aspectRatioDimension.height;

    const previousDimension = this.dimension;
    this.dimension = aspectRatioDimension;

    this.doorsData.forEach((doorData) => {
      const doorOpeningRatio = Calculator.calculateDoorOpeningRatio(
        doorData.currentDoorOpening,
        previousDimension
      );

      doorData.currentDoorOpening = Calculator.calculateDoorOpeningSize(
        doorOpeningRatio,
        this.dimension
      );

      this.updateDoorUI(doorData);
    });
  }

  private updateDoorUI(doorData: DoorData): void {
    const doorContainer = doorData.container;
    const doorOpening = doorData.currentDoorOpening;

    const xPosition =
      (this.pixiApplication.screen.width - this.dimension.width) / 2;
    const yPosition =
      (this.pixiApplication.screen.height - this.dimension.height) / 2;

    const doorImagePerspectiveMesh: PerspectiveMesh =
      doorContainer.getChildByLabel(Labels.DoorImage) as PerspectiveMesh;

    if (doorImagePerspectiveMesh) {
      doorImagePerspectiveMesh.x = xPosition;
      doorImagePerspectiveMesh.y = yPosition;

      doorImagePerspectiveMesh.setCorners(
        doorOpening.topLeft.x,
        doorOpening.topLeft.y,
        doorOpening.topRight.x,
        doorOpening.topRight.y,
        doorOpening.bottomRight.x,
        doorOpening.bottomRight.y,
        doorOpening.bottomLeft.x,
        doorOpening.bottomLeft.y
      );
    }

    const hardwareImagePerspectiveMesh: PerspectiveMesh =
      doorContainer.getChildByLabel(Labels.HardwareImage) as PerspectiveMesh;

    if (hardwareImagePerspectiveMesh) {
      hardwareImagePerspectiveMesh.x = xPosition;
      hardwareImagePerspectiveMesh.y = yPosition;

      hardwareImagePerspectiveMesh.setCorners(
        doorOpening.topLeft.x,
        doorOpening.topLeft.y,
        doorOpening.topRight.x,
        doorOpening.topRight.y,
        doorOpening.bottomRight.x,
        doorOpening.bottomRight.y,
        doorOpening.bottomLeft.x,
        doorOpening.bottomLeft.y
      );
    }

    const modificationPointsContainer: Container =
      doorContainer.getChildByLabel(Labels.ModificationPoints) as Container;

    if (modificationPointsContainer) {
      const topLeftGraphics: Graphics =
        modificationPointsContainer.getChildByLabel(
          Labels.TopLeftPoint
        ) as Graphics;

      if (topLeftGraphics) {
        topLeftGraphics.x = doorOpening.topLeft.x + xPosition;
        topLeftGraphics.y = doorOpening.topLeft.y + yPosition;
      }

      const topRightGraphics: Graphics =
        modificationPointsContainer.getChildByLabel(
          Labels.TopRightPoint
        ) as Graphics;

      if (topRightGraphics) {
        topRightGraphics.x = doorOpening.topRight.x + xPosition;
        topRightGraphics.y = doorOpening.topRight.y + yPosition;
      }

      const bottomRightGraphics: Graphics =
        modificationPointsContainer.getChildByLabel(
          Labels.BottomRightPoint
        ) as Graphics;

      if (bottomRightGraphics) {
        bottomRightGraphics.x = doorOpening.bottomRight.x + xPosition;
        bottomRightGraphics.y = doorOpening.bottomRight.y + yPosition;
      }

      const bottomLeftGraphics: Graphics =
        modificationPointsContainer.getChildByLabel(
          Labels.BottomLeftPoint
        ) as Graphics;

      if (bottomLeftGraphics) {
        bottomLeftGraphics.x = doorOpening.bottomLeft.x + xPosition;
        bottomLeftGraphics.y = doorOpening.bottomLeft.y + yPosition;
      }

      const archEdgeLeftGraphics: Graphics =
        modificationPointsContainer.getChildByLabel(
          Labels.ArchEdgeLeft
        ) as Graphics;

      if (archEdgeLeftGraphics) {
        archEdgeLeftGraphics.x = doorOpening.archEdgeLeft.x + xPosition;
        archEdgeLeftGraphics.y = doorOpening.archEdgeLeft.y + yPosition;
      }

      this.redrawModificationPoints(modificationPointsContainer, doorData);
    }

    const doorPerimeterGraphics: Graphics = doorContainer.getChildByLabel(
      Labels.DoorPerimeter
    ) as Graphics;

    if (doorPerimeterGraphics) {
      doorPerimeterGraphics.x =
        (this.pixiApplication.screen.width - this.dimension.width) / 2;
      doorPerimeterGraphics.y =
        (this.pixiApplication.screen.height - this.dimension.height) / 2;
      this.redrawDoorPerimeter(doorPerimeterGraphics, doorData);
    }
  }

  public async exportCompositeImage(
    imageFormat?: ImageFormat
  ): Promise<string> {
    this.setParameterAndModificationPointsVisibility(false);

    if (!imageFormat) {
      imageFormat = ImageFormat.PNG;
    }

    const data = this.pixiApplication.renderer.extract.base64({
      target: this.pixiApplication.stage,
      format: Mapper.ImageFormatToString(imageFormat),
      antialias: true,
      resolution: 1,
      quality: 1,
    });

    this.setParameterAndModificationPointsVisibility(true);

    return data;
  }

  public downloadCompositeImage(filename?: string): void {
    this.setParameterAndModificationPointsVisibility(false);

    this.pixiApplication.renderer.extract.download({
      target: this.pixiApplication.stage,
      filename: filename,
      antialias: true,
      resolution: 1,
    });

    this.setParameterAndModificationPointsVisibility(true);
  }

  private setParameterAndModificationPointsVisibility(visible: boolean): void {
    this.doorsData.forEach((doorData) => {
      const doorPerimeterGraphics: Graphics =
        doorData.container.getChildByLabel(Labels.DoorPerimeter) as Graphics;

      if (doorPerimeterGraphics) {
        doorPerimeterGraphics.visible = visible;
      }

      const modificationPointsContainer: Container =
        doorData.container.getChildByLabel(
          Labels.ModificationPoints
        ) as Container;

      if (modificationPointsContainer) {
        modificationPointsContainer.visible = visible;
      }
    });
  }

  private setMagnificationPosition(
    doorType: DoorType,
    modificationPointType: ModificationPointType,
    point: Point
  ): void {
    if (modificationPointType === ModificationPointType.ArchEdgeLeft) {
      return;
    }

    const magnificationPoint = this.calculateMagnificationPosition(
      point,
      this.loadDoorOptions.modificationMagnificationRadius
    );

    if (this.loadDoorOptions.modificationMagnificationBorderWidth > 0) {
      const magnificationTargetContainer =
        this.magnificationContainer.getChildByLabel(
          Labels.MagnificationTargetContainer
        ) as Container;

      if (magnificationTargetContainer) {
        this.redrawMagnificationTarget(
          doorType,
          magnificationTargetContainer,
          modificationPointType
        );
      }
    }

    if (this.magnificationContainer) {
      this.magnificationContainer.x = magnificationPoint.x;
      this.magnificationContainer.y = magnificationPoint.y;

      const magnifiedDomicileSprite =
        this.magnificationContainer.getChildByLabel(
          Labels.MagnificationImage
        ) as Sprite;

      if (magnifiedDomicileSprite) {
        const xPosition =
          (this.pixiApplication.screen.width - this.dimension.width) / 2;
        const yPosition =
          (this.pixiApplication.screen.height - this.dimension.height) / 2;

        magnifiedDomicileSprite.x =
          (0 - point.x + xPosition) *
            this.loadDoorOptions.modificationMagnificationFactor +
          this.loadDoorOptions.modificationMagnificationRadius;
        magnifiedDomicileSprite.y =
          (0 - point.y + yPosition) *
            this.loadDoorOptions.modificationMagnificationFactor +
          this.loadDoorOptions.modificationMagnificationRadius;
      }
    }
  }

  private calculateMagnificationPosition(point: Point, radius: number): Point {
    const offset = this.loadDoorOptions.modificationMagnificationOffset;
    const selectedRadius = this.loadDoorOptions.modificationPointSelectedRadius;

    //Top
    let magnifyingPoint = new Point(
      point.x - radius,
      point.y - radius * 2 - offset - selectedRadius
    );

    if (this.isValidMagnificationPosition(magnifyingPoint, radius)) {
      return magnifyingPoint;
    }

    //Left
    magnifyingPoint = new Point(
      point.x - radius * 2 - offset - selectedRadius,
      point.y - radius
    );

    if (this.isValidMagnificationPosition(magnifyingPoint, radius)) {
      return magnifyingPoint;
    }

    //Right
    magnifyingPoint = new Point(
      point.x + offset + selectedRadius,
      point.y - radius
    );

    if (this.isValidMagnificationPosition(magnifyingPoint, radius)) {
      return magnifyingPoint;
    }

    //TopLeft
    magnifyingPoint = new Point(
      point.x - radius * 2 - offset,
      point.y - radius * 2 - offset
    );

    if (this.isValidMagnificationPosition(magnifyingPoint, radius)) {
      return magnifyingPoint;
    }

    //TopRight
    magnifyingPoint = new Point(
      point.x + offset,
      point.y - radius * 2 - offset
    );

    if (this.isValidMagnificationPosition(magnifyingPoint, radius)) {
      return magnifyingPoint;
    }

    //BottomLeft
    magnifyingPoint = new Point(
      point.x - radius * 2 - offset,
      point.y + offset
    );

    if (this.isValidMagnificationPosition(magnifyingPoint, radius)) {
      return magnifyingPoint;
    }

    //BottomRight
    magnifyingPoint = new Point(point.x + offset, point.y + offset);

    if (this.isValidMagnificationPosition(magnifyingPoint, radius)) {
      return magnifyingPoint;
    }

    //Default - Top
    magnifyingPoint = new Point(
      point.x - radius,
      point.y - radius * 2 - offset - selectedRadius
    );

    return magnifyingPoint;
  }

  private isValidMagnificationPosition(point: Point, radius: number): boolean {
    if (!this.isPointWithinDomicileSprite(point)) return false;

    let testPoint: Point = new Point(point.x + radius * 2, point.y);

    if (!this.isPointWithinDomicileSprite(testPoint)) return false;

    testPoint = new Point(point.x, point.y + radius * 2);

    if (!this.isPointWithinDomicileSprite(testPoint)) return false;

    return true;
  }

  private clearMagnification(): void {
    while (this.magnificationContainer.children[0]) {
      this.magnificationContainer.removeChild(
        this.magnificationContainer.children[0]
      );
    }
  }

  private redrawMagnificationTarget(
    doorType: DoorType,
    magnificationTargetContainer: Container,
    modificationPointType: ModificationPointType
  ): void {
    let redrawGraphics = false;
    let targetGraphics: Graphics | null = null;

    let firstChild: ContainerChild | IRenderLayer | null = null;

    if (magnificationTargetContainer.children.length > 0) {
      firstChild = magnificationTargetContainer.getChildAt(0);
    }

    if (firstChild) {
      targetGraphics = firstChild as Graphics;

      if (targetGraphics) {
        const currentModificationPointType =
          Mapper.StringToModificationPointType(targetGraphics.label);

        if (currentModificationPointType != modificationPointType) {
          redrawGraphics = true;
        }
      } else {
        while (magnificationTargetContainer.children[0]) {
          magnificationTargetContainer.removeChild(
            magnificationTargetContainer.children[0]
          );
        }

        redrawGraphics = true;
      }
    } else {
      redrawGraphics = true;
    }

    if (!redrawGraphics) {
      return;
    }

    if (!targetGraphics) {
      targetGraphics = new Graphics();
      magnificationTargetContainer.addChild(targetGraphics);
    }

    targetGraphics.label = Mapper.ModificationPointTypeToString(
      modificationPointType
    );

    targetGraphics.clear();

    const offset = this.loadDoorOptions.modificationMagnificationRadius;

    const strokeInput: StrokeInput = {
      color: this.loadDoorOptions?.modificationMagnificationBorderColor.color,
      alpha: this.loadDoorOptions?.modificationMagnificationBorderColor.alpha,
      width: this.loadDoorOptions?.modificationMagnificationBorderWidth,
    };

    if (
      modificationPointType === ModificationPointType.TopLeft &&
      doorType === DoorType.ChamferedGarageDoor
    ) {
      targetGraphics
        .moveTo(offset, offset * 2)
        .lineTo(offset, offset)
        .lineTo(offset * 2, 0)
        .stroke(strokeInput);
    } else if (
      modificationPointType === ModificationPointType.TopRight &&
      doorType === DoorType.ChamferedGarageDoor
    ) {
      targetGraphics
        .moveTo(offset, offset * 2)
        .lineTo(offset, offset)
        .lineTo(0, 0)
        .stroke(strokeInput);
    } else if (
      modificationPointType === ModificationPointType.ChamferEdgeLeft
    ) {
      targetGraphics
        .moveTo(0, offset * 2)
        .lineTo(offset, offset)
        .lineTo(offset * 2, offset)
        .stroke(strokeInput);
    } else if (
      modificationPointType === ModificationPointType.ChamferEdgeRight
    ) {
      targetGraphics
        .moveTo(0, offset)
        .lineTo(offset, offset)
        .lineTo(offset * 2, offset * 2)
        .stroke(strokeInput);
    } else if (modificationPointType === ModificationPointType.TopLeft) {
      targetGraphics
        .moveTo(offset, offset * 2)
        .lineTo(offset, offset)
        .lineTo(offset * 2, offset)
        .stroke(strokeInput);
    } else if (modificationPointType === ModificationPointType.TopRight) {
      targetGraphics
        .moveTo(offset, offset * 2)
        .lineTo(offset, offset)
        .lineTo(0, offset)
        .stroke(strokeInput);
    } else if (modificationPointType === ModificationPointType.BottomLeft) {
      targetGraphics
        .moveTo(offset, 0)
        .lineTo(offset, offset)
        .lineTo(offset * 2, offset)
        .stroke(strokeInput);
    } else if (modificationPointType === ModificationPointType.BottomRight) {
      targetGraphics
        .moveTo(offset, 0)
        .lineTo(offset, offset)
        .lineTo(0, offset)
        .stroke(strokeInput);
    }
  }

  public async setDoorImage(
    doorId: number,
    doorImageUrl: string
  ): Promise<void> {
    const doorData = this.getDoorData(doorId);

    if (!doorData) {
      return;
    }

    if (doorData.door) {
      doorData.door.imageUrl = doorImageUrl;
    }

    const doorContainer = doorData.container;

    if (!doorContainer) {
      return;
    }

    const doorPerspectiveMesh = doorContainer.getChildByLabel(
      Labels.DoorImage
    ) as PerspectiveMesh;

    if (doorPerspectiveMesh) {
      let doorTexture = await Assets.load(doorImageUrl);

      if (doorData.door.doorType === DoorType.ArchedGarageDoor) {
        doorPerspectiveMesh.texture.destroy(true);

        const archDoorImageContainer: Container = doorContainer.getChildByLabel(
          Labels.ArchDoorImageContainer
        ) as Container;

        if (archDoorImageContainer) {
          const archSprite = archDoorImageContainer.getChildByLabel(
            Labels.ArchSprite
          ) as Sprite;

          if (archSprite) {
            archSprite.texture = doorTexture;

            const archMask = archDoorImageContainer.getChildByLabel(
              Labels.ArchMask
            ) as Graphics;

            if (archMask) {
              this.updateArchMaskGraphics(
                doorData.currentDoorOpening,
                archMask,
                doorTexture.width,
                doorTexture.height
              );

              doorTexture = this.generateTextureFromImageContainer(
                archDoorImageContainer
              );
            }
          }
        }
      }

      doorPerspectiveMesh.texture = doorTexture;
    }
  }

  public async setSelectedDoorImage(doorImageUrl: string): Promise<void> {
    const selectedDoorId = this.getSelectedDoorId();

    if (selectedDoorId < 0) {
      return;
    }

    await this.setDoorImage(selectedDoorId, doorImageUrl);
  }

  public async setHardwareImage(
    doorId: number,
    hardwareImageUrl: string
  ): Promise<void> {
    const doorData = this.getDoorData(doorId);

    if (!doorData) {
      return;
    }

    const removeHardwareImage =
      hardwareImageUrl === "" ||
      hardwareImageUrl === null ||
      hardwareImageUrl === undefined;

    if (doorData.door) {
      if (removeHardwareImage) {
        doorData.door.hardware = undefined;
      } else {
        if (!doorData.door.hardware) {
          doorData.door.hardware = new Hardware(hardwareImageUrl);
        } else {
          doorData.door.hardware.imageUrl = hardwareImageUrl;
        }
      }
    }

    const doorContainer = doorData.container;

    if (!doorContainer) {
      return;
    }

    const hardwarePerspectiveMesh = doorContainer.getChildByLabel(
      Labels.HardwareImage
    ) as PerspectiveMesh;

    const archHardwareImageContainer = doorContainer.getChildByLabel(
      Labels.ArchHardwareImageContainer
    ) as Container;

    if (hardwarePerspectiveMesh && removeHardwareImage) {
      doorContainer.removeChild(hardwarePerspectiveMesh);

      if (archHardwareImageContainer) {
        doorContainer.removeChild(archHardwareImageContainer);
      }

      return;
    }

    if (removeHardwareImage) {
      return;
    }

    let hardwareTexture = await Assets.load(hardwareImageUrl);

    if (hardwarePerspectiveMesh) {
      if (doorData.door.doorType === DoorType.ArchedGarageDoor) {
        hardwarePerspectiveMesh.texture.destroy(true);

        if (archHardwareImageContainer) {
          const archSprite = archHardwareImageContainer.getChildByLabel(
            Labels.ArchSprite
          ) as Sprite;

          if (archSprite) {
            archSprite.texture = hardwareTexture;

            const archMask = archHardwareImageContainer.getChildByLabel(
              Labels.ArchMask
            ) as Graphics;

            if (archMask) {
              this.updateArchMaskGraphics(
                doorData.currentDoorOpening,
                archMask,
                hardwareTexture.width,
                hardwareTexture.height
              );

              hardwareTexture = this.generateTextureFromImageContainer(
                archHardwareImageContainer
              );
            }
          }
        }
      }

      hardwarePerspectiveMesh.texture = hardwareTexture;
    } else {
      const doorPerspectiveMesh = doorContainer.getChildByLabel(
        Labels.DoorImage
      ) as PerspectiveMesh;

      if (doorPerspectiveMesh) {
        this.addHardware(
          doorData.door,
          doorContainer,
          doorData.currentDoorOpening,
          doorPerspectiveMesh,
          hardwareTexture
        );
      }
    }
  }

  public async setSelectedHardwareImage(
    hardwareImageUrl: string
  ): Promise<void> {
    const selectedDoorId = this.getSelectedDoorId();

    if (selectedDoorId < 0) {
      return;
    }

    await this.setHardwareImage(selectedDoorId, hardwareImageUrl);
  }

  public async setDoorImages(
    doorId: number,
    doorImageUrl: string,
    hardwareImageUrl: string
  ): Promise<void> {
    await this.setDoorImage(doorId, doorImageUrl);
    await this.setHardwareImage(doorId, hardwareImageUrl);
  }

  public async setSelectedDoorImages(
    doorImageUrl: string,
    hardwareImageUrl: string
  ): Promise<void> {
    const selectedDoorId = this.getSelectedDoorId();

    if (selectedDoorId < 0) {
      return;
    }

    await this.setDoorImage(selectedDoorId, doorImageUrl);
    await this.setHardwareImage(selectedDoorId, hardwareImageUrl);
  }
}
